import React, { useEffect, useState, useRef, useMemo } from "react";
import Switch from "@mui/material/Switch";
import {
  Button,
  Grid,
  Autocomplete,
  TextField,
  Card,
  CardContent,
  Typography,
  Box,
  Select,
  MenuItem,
  Tooltip,
  Checkbox,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  Slide,
  Chip,
} from "@mui/material";
import PrimeReactDataGrid from "components/PrimeReactDataGrid/PrimeReactDataGrid";
import { Close } from "@mui/icons-material";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "components/AccordionComponents/AccordionComponents";
import { fontStyles } from "components/Common/commonStyles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BackdropComponent from "../../Spinner/Spinner";
import styled from "@mui/material/styles/styled";
import ArrowCircleRightOutlinedIcon from "@mui/icons-material/ArrowCircleRightOutlined";
import SwapHorizontalCircleOutlinedIcon from "@mui/icons-material/SwapHorizontalCircleOutlined";
import SnackBar from "components/SnackBar/SnackBar";
import applicationUsageLog from "../../../utils/applicationUsagesLog";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CustomBulkOMTHeader from "./CustomBulkOMTHeader";
import TextLabel from "components/Common/TextLabel/TextLabel";
import TaskTypeChangeWarning from "./TaskTypeWarningPopup";
import moment from "moment";
import format from "date-fns/format";
import DialogBox from "components/ConfirmationDialog/DialogBox";
import HistoryIcon from "@mui/icons-material/History";
import {
  getAllParentClients,
  getAllProjectsByParentClientCode,
  getWaveDetails,
  getClassRosters,
  getClassSearchResults,
  getAllTechSolutions,
  getAllTaskTypes,
  getAllStatus,
  getBulkReBuild,
  saveOMTClassDetails,
  searchEmployeeProfile,
  cancelOMTSchedule,
  reschedulecancelledOMTSchedule,
  getOMTClassDetailsHistory,
  getConfigurationValues,
  getAllNoRosterIdClasses,
} from "../../../services/userServices";
import { GridSearchIcon } from "@mui/x-data-grid";
import { CustomTooltip } from "components/RosterConfig/RosterConfigStyle";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import { useAuth } from "context/AuthContext";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import UploadClassModal from "./UploadClassModal";

export const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ============================================================================
// STYLED COMPONENTS
// ============================================================================
const StyledChip = styled(Chip)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  maxWidth: "160px",
  backgroundColor: "#E3F2FD",
  color: "#3299FF",
  "& .MuiChip-deleteIcon": {
    color: "#3299FF",
  },
}));

const LimitChip = styled(Chip)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  fontWeight: 600,
  backgroundColor: "#3299FF",
  color: "white",
}));

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

const CustomOutlinedButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  "&:hover": {
    borderColor: "#3299FF",
    backgroundColor: "#E0F2FF",
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "calc(100vh - 300px)",
  borderTop: `2px solid ${theme.palette.divider}`,
  borderLeft: `2px solid ${theme.palette.divider}`,
  borderRight: `2px solid ${theme.palette.divider}`,
  "& .MuiTable-root": {
    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
    fontSize: "12px",
    letterSpacing: "0.10em",
  },
  "& .MuiTableCell-head": {
    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
    fontSize: "12px",
    letterSpacing: "0.10em",
    fontWeight: "bold",
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    position: "sticky",
    top: 0,
    zIndex: 10,
    padding: "8px 12px",
    height: "48px",
    minWidth: "150px",
  },
  "& .MuiTableCell-body": {
    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
    fontSize: "12px",
    letterSpacing: "0.10em",
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    padding: "8px 12px",
    height: "48px",
    minWidth: "150px",
  },
  "& .sticky-column": {
    position: "sticky",
    left: 0,
    zIndex: 5,
    backgroundColor: "#fff",
    minWidth: "58px",
  },
  "& .sticky-column-rid": {
    position: "sticky",
    left: "138px",
    zIndex: 5,
    backgroundColor: "#fff",
    minWidth: "70px",
    width: "70px",
  },
  "& .MuiTableCell-head.sticky-column-rid": {
    zIndex: 11,
  },
  "& .sticky-column-rid-no-checkbox": {
    position: "sticky",
    left: "80px",
    zIndex: 5,
    backgroundColor: "#fff",
    minWidth: "70px",
    width: "70px",
  },
  "& .MuiTableCell-head.sticky-column-rid-no-checkbox": {
    zIndex: 11,
  },
  "& .MuiTableCell-head.sticky-column": {
    zIndex: 11,
  },
  "& .sticky-column-actions": {
    position: "sticky",
    left: "58px",
    zIndex: 5,
    backgroundColor: "#fff",
    minWidth: "80px",
    width: "80px",
  },
  "& .MuiTableCell-head.sticky-column-actions": {
    zIndex: 11,
  },
  "& .sticky-column-actions-no-checkbox": {
    position: "sticky",
    left: "0px",
    zIndex: 5,
    backgroundColor: "#fff",
    minWidth: "80px",
    width: "80px",
  },
  "& .MuiTableCell-head.sticky-column-actions-no-checkbox": {
    zIndex: 11,
  },
  "& .sticky-column-1": {
    position: "sticky",
    left: "208px",
    zIndex: 5,
    backgroundColor: "#fff",
    minWidth: "180px",
    width: "180px",
  },
  "& .MuiTableCell-head.sticky-column-1": {
    zIndex: 11,
  },
  "& .sticky-column-1-no-checkbox": {
    position: "sticky",
    left: "150px",
    zIndex: 5,
    backgroundColor: "#fff",
    minWidth: "180px",
    width: "180px",
  },
  "& .MuiTableCell-head.sticky-column-1-no-checkbox": {
    zIndex: 11,
  },
  "& .sticky-column-2": {
    position: "sticky",
    left: "388px",
    zIndex: 5,
    backgroundColor: "#fff",
    minWidth: "160px",
    width: "160px",
  },
  "& .MuiTableCell-head.sticky-column-2": {
    zIndex: 11,
  },
  "& .sticky-column-2-no-checkbox": {
    position: "sticky",
    left: "330px",
    zIndex: 5,
    backgroundColor: "#fff",
    minWidth: "160px",
    width: "160px",
  },
  "& .MuiTableCell-head.sticky-column-2-no-checkbox": {
    zIndex: 11,
  },
  // No-roster overrides — RID column widens to 160px, shift Name and Status columns right by 90px
  "& .sticky-column-rid.no-roster-class": {
    minWidth: "160px",
    width: "160px",
  },
  "& .sticky-column-rid-no-checkbox.no-roster-class": {
    minWidth: "160px",
    width: "160px",
  },
  "& .sticky-column-1.no-roster-class": {
    left: "298px",
  },
  "& .sticky-column-1-no-checkbox.no-roster-class": {
    left: "240px",
  },
  "& .sticky-column-2.no-roster-class": {
    left: "478px",
  },
  "& .sticky-column-2-no-checkbox.no-roster-class": {
    left: "420px",
  },
  "& .sticky-column.unsaved, & .sticky-column-rid.unsaved,& .sticky-column-1.unsaved, & .sticky-column-2.unsaved, & .sticky-column-actions.unsaved":
    {
      backgroundColor: "#ffecb3",
    },
  "& .sticky-column.saved,  & .sticky-column-rid.saved, & .sticky-column-1.saved, & .sticky-column-2.saved, & .sticky-column-actions.saved":
    {
      backgroundColor: "#d4edda",
    },
  "& .sticky-column.invalid-cell,  & .sticky-column-rid.invalid-cell,& .sticky-column-1.invalid-cell, & .sticky-column-2.invalid-cell, & .sticky-column-actions.invalid-cell":
    {
      backgroundColor: "#ffe6e6",
    },
  "& .unsaved": {
    backgroundColor: "#ffecb3",
    color: "#000",
  },
  "& .saved": {
    animation: "fadeToTransparent 5s forwards",
    backgroundColor: "#d4edda",
    color: "#155724",
  },
  "& .invalid-cell": {
    backgroundColor: "#ffe6e6 !important",
  },
  "@keyframes fadeToTransparent": {
    "0%": { backgroundColor: "#d4edda", color: "#155724" },
    "100%": { backgroundColor: "transparent", color: "inherit" },
  },
}));

const MainHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: 18,
  fontWeight: 800,
  letterSpacing: "0.10em",
  paddingBottom: theme.spacing(1),
}));

const ScrollableTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  "& .MuiTooltip-tooltip": {
    maxWidth: "400px",
    maxHeight: "300px",
    overflow: "auto",
    padding: "12px",
    backgroundColor: "rgba(97, 97, 97, 0.95)",
    fontSize: "13px",
    lineHeight: "1.5",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(255, 255, 255, 0.3)",
      borderRadius: "4px",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.5)",
      },
    },
  },
});

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const BuildDescriptionCell = ({
  row,
  buildOptions,
  setBuildOptions,
  onUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const techSolutionId = row?.OMTTechSolutionId;

  useEffect(() => {
    let cancelled = false;

    const fetchBuildOptions = async () => {
      if (techSolutionId) {
        if (!cancelled) {
          setLoading(true);
        }

        try {
          const buildResponse = await getBulkReBuild(techSolutionId);

          if (cancelled) return;

          if (buildResponse?.data?.message === "Success") {
            if (!cancelled) {
              setBuildOptions(buildResponse.data.data);
            }
          }
        } catch (err) {
          if (!cancelled) {
            console.error("Error fetching build options:", err);
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
      }
    };

    fetchBuildOptions();

    return () => {
      cancelled = true;
    };
  }, [techSolutionId]);

  return (
    <Select
      fullWidth
      value={row.BuildDescription || ""}
      onChange={(event) => {
        const selectedBuild = buildOptions.find(
          (opt) => opt.BuildDescription === event.target.value,
        );
        onUpdate({
          BuildDescription: event.target.value,
          OMTBuildId: selectedBuild?.OMTBuildId || null,
        });
      }}
      disabled={loading}
      size="small"
    >
      {(Array.isArray(buildOptions) ? buildOptions : []).map((opt) => (
        <MenuItem key={opt.OMTBuildId} value={opt.BuildDescription}>
          {opt.BuildDescription}
        </MenuItem>
      ))}
    </Select>
  );
};

// ============================================================================
// HELPER FUNCTIONS - History
// ============================================================================

const extractHistoryData = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.data)) return response.data;
  if (response.statusCode === 200 && response.data) {
    return Array.isArray(response.data) ? response.data : [];
  }
  if (response.message === "Success" && response.data) {
    return Array.isArray(response.data) ? response.data : [];
  }
  if (response.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  return [];
};

const sortHistoryByDate = (historyData) => {
  return historyData.sort((a, b) => {
    return new Date(b.ModifiedDate) - new Date(a.ModifiedDate);
  });
};

const handleSuccessfulHistory = (historyData, sethistoryData, setSnack) => {
  const sortedData = sortHistoryByDate(historyData);
  sethistoryData(sortedData);
  setSnack({
    open: true,
    message: `Found ${sortedData.length} change record(s)`,
    severity: "success",
  });
};

const handleEmptyHistory = (sethistoryData, setSnack) => {
  sethistoryData([]);
  setSnack({
    open: true,
    message: "No schedule change history found",
    severity: "info",
  });
};

const handleHistoryError = (error, sethistoryData, setSnack) => {
  console.error("Error fetching schedule history:", error);
  sethistoryData([]);
  setSnack({
    open: true,
    message: "Failed to fetch schedule change history",
    severity: "error",
  });
};

// ============================================================================
// HELPER FUNCTIONS - Date Validation
// ============================================================================

const getScheduleDates = (field, value, currentRow) => {
  const dateMap = {
    ScheduleStart: {
      start: value,
      end: currentRow?.ScheduleEnd,
      paired: "ScheduleEnd",
    },
    ScheduleEnd: {
      start: currentRow?.ScheduleStart,
      end: value,
      paired: "ScheduleStart",
    },
    EXTScheduleStart: {
      start: value,
      end: currentRow?.EXTScheduleEnd,
      paired: "EXTScheduleEnd",
    },
    EXTScheduleEnd: {
      start: currentRow?.EXTScheduleStart,
      end: value,
      paired: "EXTScheduleStart",
    },
    PERRScheduleStart: {
      start: value,
      end: currentRow?.PERRScheduleEnd,
      paired: "PERRScheduleEnd",
    },
    PERRScheduleEnd: {
      start: currentRow?.PERRScheduleStart,
      end: value,
      paired: "PERRScheduleStart",
    },
    RecoveryScheduleStart: {
      start: value,
      end: currentRow?.RecoveryScheduleEnd,
      paired: "RecoveryScheduleEnd",
    },
    RecoveryScheduleEnd: {
      start: currentRow?.RecoveryScheduleStart,
      end: value,
      paired: "RecoveryScheduleStart",
    },
  };

  return dateMap[field] || null;
};

const isDateRangeInvalid = (startDate, endDate) => {
  const formatStartDateTime = moment(new Date(startDate));
  const formatEndDateTime = moment(new Date(endDate));

  return (
    formatStartDateTime.isValid() &&
    formatEndDateTime.isValid() &&
    (formatStartDateTime.isAfter(formatEndDateTime) ||
      formatEndDateTime.isBefore(formatStartDateTime) ||
      formatEndDateTime.diff(formatStartDateTime, "year", true) > 1)
  );
};

const updateInvalidCellsForDates = (
  setInvalidCells,
  rowId,
  field,
  pairedField,
  isInvalid,
) => {
  setInvalidCells((prev) => {
    const updated = {
      ...prev,
      [rowId]: {
        ...(prev[rowId] || {}),
      },
    };

    if (isInvalid) {
      updated[rowId][field] = true;
    } else {
      delete updated[rowId]?.[field];
      delete updated[rowId]?.[pairedField];

      if (updated[rowId] && Object.keys(updated[rowId]).length === 0) {
        delete updated[rowId];
      }
    }

    return updated;
  });
};

const validateAndUpdateDateCell = (
  field,
  value,
  currentRow,
  rowId,
  setInvalidCells,
  setSnack,
) => {
  if (!field.includes("Schedule")) {
    return;
  }

  const scheduleDates = getScheduleDates(field, value, currentRow);
  if (!scheduleDates) return;

  const { start: startDate, end: endDate, paired: pairedField } = scheduleDates;

  if (startDate && endDate) {
    const isInvalid = isDateRangeInvalid(startDate, endDate);

    updateInvalidCellsForDates(
      setInvalidCells,
      rowId,
      field,
      pairedField,
      isInvalid,
    );

    if (isInvalid) {
      setSnack({
        open: true,
        message: "Start date must be before end date and within 1 year.",
        severity: "error",
      });
    }
  }
};

// ============================================================================
// HELPER FUNCTIONS - Bulk Edit
// ============================================================================

const findFieldGroup = (key, autocompleteFieldGroups) => {
  return Object.entries(autocompleteFieldGroups).find(([groupKey, fields]) =>
    fields.includes(key),
  );
};

const categorizeKey = (
  key,
  autocompleteFieldGroups,
  countedGroups,
  independentFields,
) => {
  const matchedGroup = findFieldGroup(key, autocompleteFieldGroups);

  if (matchedGroup) {
    countedGroups.add(matchedGroup[0]);
  } else {
    independentFields.add(key);
  }
};

const processCellKeys = (cell, autocompleteFieldGroups) => {
  const countedGroups = new Set();
  const independentFields = new Set();

  Object.keys(cell).forEach((key) => {
    categorizeKey(
      key,
      autocompleteFieldGroups,
      countedGroups,
      independentFields,
    );
  });

  return countedGroups.size + independentFields.size;
};

const updateRowForASDLead = (row, value) => ({
  ...row,
  ASDLeadEmail: value.Email,
  ASDLeadOracleId: value.OracleId,
  ASDLead: value.Email,
});

const updateRowForTaskType = (row, value) => ({
  ...row,
  OMTTaskTypeId: value.OMTTaskTypeId,
  TaskTypeDescription: value.TaskTypeDescription,
});

const updateRowForFinalStatus = (row, value) => ({
  ...row,
  OMTFinalStatusId: value.OMTFinalStatusId,
  FinalStatusDescription: value.FinalStatusDescription,
});

const updateRowForComments = (row, value) => ({
  ...row,
  Comments: value,
});

const updateRowForBuild = (row, value) => ({
  ...row,
  OMTBuildId: value.OMTBuildId,
  BuildDescription: value.BuildDescription,
});

const updateRowForTechSolution = (row, value) => {
  return {
    ...row,
    OMTTechSolutionId: value.OMTTechSolutionId,
    TechSolutionDescription: value.TechSolutionDescription,
    ...(value.clearBuild && { BuildDescription: "", OMTBuildId: null }),
  };
};

const updateRowForScheduleStatus = (row, field, value) => {
  const idField = field.replace("Status", "StatusId");
  const fullIdField = `OMT${idField}`;
  return {
    ...row,
    [field]: value.FinalStatusDescription,
    [fullIdField]: value.OMTFinalStatusId,
  };
};

const updateRowForGenericField = (row, field, value) => ({
  ...row,
  [field]: value,
});

const getUpdatedRowForField = (row, field, value) => {
  if (field === "ASDLead" && value) {
    return updateRowForASDLead(row, value);
  }

  if (
    field === "TaskTypeDescription" &&
    value &&
    value.OMTTaskTypeId !== undefined
  ) {
    return updateRowForTaskType(row, value);
  }

  if (
    field === "FinalStatusDescription" &&
    value &&
    value.OMTFinalStatusId !== undefined
  ) {
    return updateRowForFinalStatus(row, value);
  }

  if (field === "Comments" && value !== undefined) {
    return updateRowForComments(row, value);
  }

  if (field === "BuildDescription" && value && value.OMTBuildId !== undefined) {
    return updateRowForBuild(row, value);
  }

  if (
    field === "TechSolutionDescription" &&
    value &&
    value.OMTTechSolutionId !== undefined
  ) {
    return updateRowForTechSolution(row, value);
  }

  const isScheduleStatus = [
    "ScheduleStatus",
    "EXTScheduleStatus",
    "PERRScheduleStatus",
    "RecoveryScheduleStatus",
  ].includes(field);

  if (isScheduleStatus && value && value.OMTFinalStatusId !== undefined) {
    return updateRowForScheduleStatus(row, field, value);
  }

  if (value !== row[field]) {
    return updateRowForGenericField(row, field, value);
  }

  return row;
};

const processRowForBulkEdit = (
  row,
  selectedRows,
  field,
  value,
  setInvalidCells,
  setSnack,
  getRowIdFn,
) => {
  const rowId = getRowIdFn ? getRowIdFn(row) : row.ClassTraineeId;
  if (!selectedRows.includes(rowId)) {
    return row;
  }

  const updatedRow = getUpdatedRowForField(row, field, value);

  if (field.includes("Schedule") && value) {
    const scheduleDates = getScheduleDates(field, value, row);

    if (scheduleDates) {
      const {
        start: startDate,
        end: endDate,
        paired: pairedField,
      } = scheduleDates;

      if (startDate && endDate) {
        const isInvalid = isDateRangeInvalid(startDate, endDate);

        if (isInvalid) {
          updateInvalidCellsForDates(
            setInvalidCells,
            rowId,
            field,
            pairedField,
            true,
          );
          return {
            updatedRow,
            hasInvalidDates: true,
            invalidRowId: rowId,
          };
        }

        updateInvalidCellsForDates(
          setInvalidCells,
          rowId,
          field,
          pairedField,
          false,
        );
      }
    }
  }

  return { updatedRow, hasInvalidDates: false };
};

// ============================================================================
// HELPER FUNCTIONS - Save Data
// ============================================================================

const getVisibleFieldsForTaskType = (taskTypeDesc) => {
  const pcWrapperFields = [
    "EmployeeName",
    "TraineeStatus",
    "ASDLead",
    "TechSolutionDescription",
    "BuildDescription",
    "TaskTypeDescription",
    "ScheduleStart",
    "ScheduleEnd",
    "ScheduleStatus",
    "TicketNumber",
    "FinalStatusDescription",
    "Comments",
  ];

  const tciFields = [
    "EmployeeName",
    "TraineeStatus",
    "ASDLead",
    "TechSolutionDescription",
    "BuildDescription",
    "TaskTypeDescription",
    "ScheduleStart",
    "ScheduleEnd",
    "ScheduleStatus",
    "EXT",
    "EXTScheduleStart",
    "EXTScheduleEnd",
    "EXTScheduleStatus",
    "PERR",
    "PERRScheduleStart",
    "PERRScheduleEnd",
    "PERRScheduleStatus",
    "TicketNumber",
    "FinalStatusDescription",
    "Comments",
  ];

  const ttdFields = [
    "EmployeeName",
    "TraineeStatus",
    "ASDLead",
    "TechSolutionDescription",
    "BuildDescription",
    "TaskTypeDescription",
    "ScheduleStart",
    "ScheduleEnd",
    "ScheduleStatus",
    "Recovery",
    "RecoveryScheduleStart",
    "RecoveryScheduleEnd",
    "RecoveryScheduleStatus",
    "TicketNumber",
    "FinalStatusDescription",
    "Comments",
  ];

  if (taskTypeDesc === "PC wrapper" || taskTypeDesc === "Migration") {
    return pcWrapperFields;
  }

  if (taskTypeDesc === "TCI" || taskTypeDesc === "ORT") {
    return tciFields;
  }

  if (taskTypeDesc === "TTD") {
    return ttdFields;
  }

  return pcWrapperFields;
};

const nullifyFieldValue = (field) => {
  const booleanFields = ["EXT", "PERR", "Recovery"];

  if (booleanFields.includes(field)) {
    return false;
  }

  if (field.includes("Schedule") || field.includes("StatusId")) {
    return null;
  }

  return null;
};

const nullifyFieldsForTaskType = (row, visibleFields) => {
  const nullifiableFields = [
    "EXT",
    "EXTScheduleStart",
    "EXTScheduleEnd",
    "EXTScheduleStatus",
    "OMTEXTScheduleStatusId",
    "EXTScheduleFailureReason",
    "PERR",
    "PERRScheduleStart",
    "PERRScheduleEnd",
    "PERRScheduleStatus",
    "OMTPERRScheduleStatusId",
    "PERRScheduleFailureReason",
    "Recovery",
    "RecoveryScheduleStart",
    "RecoveryScheduleEnd",
    "RecoveryScheduleStatus",
    "OMTRecoveryScheduleStatusId",
    "RecoveryScheduleFailureReason",
  ];

  const updatedRow = { ...row };

  nullifiableFields.forEach((field) => {
    if (!visibleFields.includes(field)) {
      updatedRow[field] = nullifyFieldValue(field);
    }
  });

  return updatedRow;
};

const processRowForSave = (row, editedCells, selectedRows, getRowIdFn) => {
  const rowId = getRowIdFn ? getRowIdFn(row) : row.ClassTraineeId;
  if (!selectedRows.includes(rowId) || !editedCells[rowId]) {
    return row;
  }

  if (!editedCells[rowId]?.TaskTypeDescription) {
    return row;
  }

  const taskTypeDesc = row.TaskTypeDescription;
  if (!taskTypeDesc) return row;

  const visibleFields = getVisibleFieldsForTaskType(taskTypeDesc);
  return nullifyFieldsForTaskType(row, visibleFields);
};

// ============================================================================
// HELPER FUNCTIONS - Cancel/Reschedule
// ============================================================================

const createCancelRequestBody = (row, scheduleType) => {
  const getValue = (type) => {
    if (scheduleType === type) return "True";
    const fieldMap = {
      schedule: row.IsScheduleCancelled,
      ext: row.IsEXTScheduleCancelled,
      perr: row.IsPERRScheduleCancelled,
      recovery: row.IsRecoveryScheduleCancelled,
    };
    return fieldMap[type] ? "True" : "False";
  };

  return [
    {
      OMTClassDetailsId: row.OMTClassDetailsId,
      IsScheduleCancelled: getValue("schedule"),
      IsEXTScheduleCancelled: getValue("ext"),
      IsPERRScheduleCancelled: getValue("perr"),
      IsRecoveryScheduleCancelled: getValue("recovery"),
    },
  ];
};

const updateRowAfterCancel = (row, requestBody) => ({
  ...row,
  IsScheduleCancelled: requestBody[0].IsScheduleCancelled === "True",
  IsEXTScheduleCancelled: requestBody[0].IsEXTScheduleCancelled === "True",
  IsPERRScheduleCancelled: requestBody[0].IsPERRScheduleCancelled === "True",
  IsRecoveryScheduleCancelled:
    requestBody[0].IsRecoveryScheduleCancelled === "True",
});

const createRescheduleRequestBody = (row, scheduleType) => {
  const baseBody = {
    OMTClassDetailsId: row.OMTClassDetailsId,
  };

  if (scheduleType === "schedule") {
    return [
      {
        ...baseBody,
        IsReScheduleCancelled: "True",
        ScheduleStart: null,
        ScheduleEnd: null,
        ScheduleFailureReason: null,
      },
    ];
  }

  if (scheduleType === "ext") {
    return [
      {
        ...baseBody,
        IsReEXTScheduleCancelled: "True",
        EXTScheduleStart: null,
        EXTScheduleEnd: null,
        EXTScheduleFailureReason: null,
      },
    ];
  }

  if (scheduleType === "perr") {
    return [
      {
        ...baseBody,
        IsRePERRScheduleCancelled: "True",
        PERRScheduleStart: null,
        PERRScheduleEnd: null,
        PERRScheduleFailureReason: null,
      },
    ];
  }

  if (scheduleType === "recovery") {
    return [
      {
        ...baseBody,
        IsReRecoveryScheduleCancelled: "True",
        RecoveryScheduleStart: null,
        RecoveryScheduleEnd: null,
        RecoveryScheduleFailureReason: null,
      },
    ];
  }

  return [baseBody];
};

const updateRowAfterReschedule = (row, scheduleType) => {
  const updatedRow = { ...row };

  if (scheduleType === "schedule") {
    updatedRow.IsScheduleCancelled = false;
    updatedRow.ScheduleStart = null;
    updatedRow.ScheduleEnd = null;
    updatedRow.ScheduleFailureReason = null;
  }

  if (scheduleType === "ext") {
    updatedRow.IsEXTScheduleCancelled = false;
    updatedRow.EXTScheduleStart = null;
    updatedRow.EXTScheduleEnd = null;
    updatedRow.EXTScheduleFailureReason = null;
  }

  if (scheduleType === "perr") {
    updatedRow.IsPERRScheduleCancelled = false;
    updatedRow.PERRScheduleStart = null;
    updatedRow.PERRScheduleEnd = null;
    updatedRow.PERRScheduleFailureReason = null;
  }

  if (scheduleType === "recovery") {
    updatedRow.IsRecoveryScheduleCancelled = false;
    updatedRow.RecoveryScheduleStart = null;
    updatedRow.RecoveryScheduleEnd = null;
    updatedRow.RecoveryScheduleFailureReason = null;
  }

  return updatedRow;
};

// ============================================================================
// HELPER FUNCTIONS - Render Cell Components
// ============================================================================

const CancelScheduleButton = ({
  row,
  field,
  scheduleType,
  editAccess,
  handleRescheduleClick,
  handleDoNotDisturbClick,
}) => {
  const isCancelled =
    scheduleType === "schedule"
      ? row.IsScheduleCancelled
      : scheduleType === "ext"
        ? row.IsEXTScheduleCancelled
        : scheduleType === "perr"
          ? row.IsPERRScheduleCancelled
          : row.IsRecoveryScheduleCancelled;

  const isStartField =
    (scheduleType === "schedule" && field === "ScheduleStart") ||
    (scheduleType === "ext" && field === "EXTScheduleStart") ||
    (scheduleType === "perr" && field === "PERRScheduleStart") ||
    (scheduleType === "recovery" && field === "RecoveryScheduleStart");

  if (!isStartField) return null;

  return (
    <Tooltip
      title={isCancelled ? "Reschedule cancelled schedule" : "Cancel Schedule"}
    >
      <Box component="span" sx={{ display: "flex", alignItems: "center" }}>
        {editAccess && (
          <DoNotDisturbIcon
            color={isCancelled ? "error" : "disabled"}
            fontSize="small"
            sx={{ cursor: "pointer", fontSize: "30px" }}
            onClick={(e) => {
              e.stopPropagation();
              if (isCancelled) {
                handleRescheduleClick(row.id, scheduleType);
              } else {
                handleDoNotDisturbClick(row.id, scheduleType);
              }
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

const ScheduleDateCell = ({
  row,
  field,
  cellValue,
  scheduleType,
  editAccess,
  handleRescheduleClick,
  handleDoNotDisturbClick,
}) => {
  const isCancelled =
    scheduleType === "schedule"
      ? row.IsScheduleCancelled
      : scheduleType === "ext"
        ? row.IsEXTScheduleCancelled
        : scheduleType === "perr"
          ? row.IsPERRScheduleCancelled
          : row.IsRecoveryScheduleCancelled;

  const displayValue = isCancelled
    ? "--/--/---- --:--"
    : cellValue
      ? format(new Date(cellValue), "MM/dd/yyyy hh:mm a")
      : "";
  const showButton = (cellValue && cellValue !== null) || isCancelled;

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <span>{displayValue}</span>
      <Box flexGrow={1} />
      {showButton && (
        <CancelScheduleButton
          row={row}
          field={field}
          scheduleType={scheduleType}
          editAccess={editAccess}
          handleRescheduleClick={handleRescheduleClick}
          handleDoNotDisturbClick={handleDoNotDisturbClick}
        />
      )}
    </Box>
  );
};

const CommentsCell = ({ cellValue }) => (
  <Box display="flex" alignItems="center" gap={1}>
    <ScrollableTooltip
      title={cellValue || ""}
      placement="top"
      arrow
      enterDelay={300}
    >
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          lineHeight: "1.5",
          maxHeight: "3em",
          cursor: cellValue ? "pointer" : "default",
          wordBreak: "break-word",
        }}
      >
        {cellValue || ""}
      </Box>
    </ScrollableTooltip>
  </Box>
);

const CheckboxCell = ({ cellValue }) => (
  <Box display="flex" justifyContent="center" alignItems="center">
    <Typography sx={{ fontSize: "18px", color: "default" }}>
      {cellValue ? "✓" : "✗"}
    </Typography>
  </Box>
);

const CommentsEditCell = ({
  row,
  rowId,
  field,
  comments,
  setComments,
  handleCellUpdate,
  setEditingCell,
}) => {
  const maxLength = 10000;

  return (
    <TextField
      fullWidth
      size="small"
      multiline
      maxRows={4}
      value={comments}
      autoFocus
      inputProps={{ maxLength }}
      placeholder="Enter Comments"
      helperText={`${comments.length}/${maxLength}`}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Escape") {
          setEditingCell({ rowId: null, field: null });
          setComments("");
        }
      }}
      onBlur={() => {
        handleCellUpdate(rowId, field, comments);
        setEditingCell({ rowId: null, field: null });
        setComments("");
      }}
      onChange={(e) => {
        e.stopPropagation();
        setComments(e.target.value);
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

const TicketNumberEditCell = ({
  rowId,
  field,
  cellValue,
  handleCellUpdate,
  setEditingCell,
}) => {
  const maxLength = 50;
  const [value, setValue] = useState(cellValue || "");

  return (
    <TextField
      fullWidth
      size="small"
      value={value}
      autoFocus
      inputProps={{ maxLength }}
      placeholder="Enter Ticket number"
      helperText={`${value.length}/${maxLength}`}
      onKeyDown={(e) => e.stopPropagation()}
      onBlur={() => {
        handleCellUpdate(rowId, field, value);
        setEditingCell({ rowId: null, field: null });
      }}
      onChange={(e) => {
        e.stopPropagation();
        setValue(e.target.value.replace(/[^a-zA-Z0-9]/g, ""));
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

const ASDLeadEditCell = ({
  rowId,
  searchResults,
  searchCriteria,
  setSearchCriteria,
  setEditingCell,
  onPeopleSearch,
  handleCellUpdate,
  row,
  setSearchResults,
}) => (
  <Autocomplete
    disablePortal
    fullWidth
    options={searchResults}
    size="small"
    autoFocus
    onBlur={() => setEditingCell({ rowId: null, field: null })}
    onChange={(event, value) => {
      if (value) {
        handleCellUpdate(rowId, "ASDLeadEmail", value.Email);
        handleCellUpdate(rowId, "ASDLeadOracleId", value.OracleId);
        handleCellUpdate(rowId, "ASDLead", value.Email);
      }
      setSearchCriteria("");
      setSearchResults([]);
    }}
    getOptionLabel={(option) => option.Display || ""}
    renderInput={(params) => (
      <TextField
        {...params}
        size="small"
        placeholder="Search ASD"
        value={searchCriteria || ""}
        onChange={(event) => setSearchCriteria(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            if (searchCriteria.trim().length >= 3) {
              onPeopleSearch(event, searchCriteria);
            }
          }
        }}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <InputAdornment position="end">
              <CustomTooltip title="Search">
                <IconButton
                  type="button"
                  size="small"
                  onClick={(event) => onPeopleSearch(event, searchCriteria)}
                >
                  <GridSearchIcon />
                </IconButton>
                {params.InputProps.endAdornment}
              </CustomTooltip>
            </InputAdornment>
          ),
        }}
      />
    )}
  />
);

const TechSolutionEditCell = ({
  rowId,
  cellValue,
  techOptions,
  setEditingCell,
  handleCellUpdate,
  row,
  onTechSolutionDescriptionChange,
}) => (
  <Select
    fullWidth
    size="small"
    value={cellValue || ""}
    autoFocus
    onBlur={() => setEditingCell({ rowId: null, field: null })}
    onChange={async (event) => {
      const selectedTech = techOptions.find(
        (opt) => opt.TechSolutionDescription === event.target.value,
      );
      handleCellUpdate(rowId, "TechSolutionDescription", event.target.value);
      handleCellUpdate(
        rowId,
        "OMTTechSolutionId",
        selectedTech?.OMTTechSolutionId || "",
      );

      const hasBuildOptions =
        await onTechSolutionDescriptionChange(selectedTech);

      if (!hasBuildOptions) {
        handleCellUpdate(rowId, "BuildDescription", null);
        handleCellUpdate(rowId, "OMTBuildId", null);
      }
    }}
  >
    {techOptions.map((opt) => (
      <MenuItem key={opt.OMTTechSolutionId} value={opt.TechSolutionDescription}>
        {opt.TechSolutionDescription}
      </MenuItem>
    ))}
  </Select>
);

const StatusEditCell = ({
  rowId,
  field,
  cellValue,
  statusOptions,
  setEditingCell,
  handleCellUpdate,
  row,
}) => (
  <Select
    fullWidth
    size="small"
    value={cellValue || ""}
    autoFocus
    onBlur={() => setEditingCell({ rowId: null, field: null })}
    onChange={(event) => {
      const selectedStatus = statusOptions.find(
        (opt) => opt.FinalStatusDescription === event.target.value,
      );
      handleCellUpdate(rowId, field, event.target.value || null);

      if (field === "FinalStatusDescription") {
        handleCellUpdate(
          rowId,
          "OMTFinalStatusId",
          selectedStatus?.OMTFinalStatusId || null,
        );
      }
    }}
  >
    <MenuItem value="">
      <em style={{ color: "#aaa" }}>None</em>
    </MenuItem>
    {statusOptions.map((opt) => (
      <MenuItem
        key={opt.OMTFinalStatusId ?? "blank"}
        value={opt.FinalStatusDescription}
      >
        {opt.FinalStatusDescription}
      </MenuItem>
    ))}
  </Select>
);

const ScheduleEditCell = ({
  rowId,
  row,
  field,
  cellValue,
  tempDateValues,
  setTempDateValues,
  handleCellUpdate,
  setEditingCell,
  editingCell,
}) => {
  const tempKey = `${rowId}-${field}`;
  const isEditingThisCell =
    editingCell.rowId === rowId && editingCell.field === field;
  const tempValue = isEditingThisCell
    ? (tempDateValues[tempKey] ?? cellValue)
    : cellValue;

  let formattedValue = "";
  if (tempValue) {
    try {
      const date = new Date(tempValue);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        formattedValue = `${year}-${month}-${day}T${hours}:${minutes}`;
      }
    } catch (e) {
      console.error("Date formatting error:", e);
    }
  }

  return (
    <TextField
      size="small"
      type="datetime-local"
      value={formattedValue}
      sx={{
        backgroundColor: "#fff",
        width: 200,
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: "",
          },
        },
      }}
      InputLabelProps={{ shrink: true }}
      onChange={(e) => {
        const newValue = e.target.value === "" ? null : e.target.value;
        setTempDateValues((prev) => ({
          ...prev,
          [tempKey]: newValue,
        }));
      }}
      onBlur={(e) => {
        const newValue = e.target.value === "" ? null : e.target.value;
        handleCellUpdate(rowId, field, newValue);
        setEditingCell({ rowId: null, field: null });
        setTempDateValues((prev) => {
          const updated = { ...prev };
          delete updated[tempKey];
          return updated;
        });
      }}
      onFocus={(e) => {
        e.stopPropagation();
        setEditingCell({ rowId: rowId, field: field });
      }}
      onClick={(e) => e.stopPropagation()}
      InputProps={{ sx: { ...fontStyles } }}
      fullWidth
    />
  );
};

const CheckboxEditCell = ({ rowId, field, cellValue, handleCellUpdate }) => (
  <Box display="flex" justifyContent="center" alignItems="center">
    <Checkbox
      checked={cellValue || false}
      onChange={(event) => handleCellUpdate(rowId, field, event.target.checked)}
    />
  </Box>
);

const DefaultEditCell = ({
  rowId,
  field,
  cellValue,
  setEditingCell,
  handleCellUpdate,
}) => (
  <TextField
    fullWidth
    size="small"
    value={cellValue || ""}
    autoFocus
    onBlur={() => setEditingCell({ rowId: null, field: null })}
    onChange={(e) => handleCellUpdate(rowId, field, e.target.value)}
  />
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const OMTClassSchedule = (props) => {
  const { hasPermission } = useAuth();

  const [loading, setLoading] = useState(false);
  const [wave, setWave] = useState({ Wave: "" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rows, setRows] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [project, setProject] = useState({ ProjectName: "", ProjectCode: "" });
  const [waveOptions, setWaveOptions] = useState([]);
  const [parentClientOptions, setParentClientOptions] = useState([]);
  const [parentClient, setParentClient] = useState({ ParentClientName: "" });
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [filterRosterData, setFilterRosterData] = useState([]);
  const [rosterValue, setRosterValue] = useState([]);
  const [taskOptions, setTaskOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedTechSolutionId, setSelectedTechSolutionId] = useState("");
  const [buildOptions, setBuildOptions] = useState([]);
  const [techOptions, setTechOptions] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [editedCells, setEditedCells] = useState({});
  const [invalidCells, setInvalidCells] = useState({});
  const [savedCells, setSavedCells] = useState({});
  const [headerResetTrigger, setHeaderResetTrigger] = useState(false);
  const [selectedTaskTypeId, setSelectedTaskTypeId] = useState("");
  const [totalUnsavedCells, setTotalUnsavedCells] = useState(0);
  const [editingCell, setEditingCell] = useState({ rowId: null, field: null });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState(null);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogMessageType, setDialogMessageType] = useState("Confirmation");
  const [dialogBulletList, setDialogBulletList] = useState([]);
  const [isConfirmationDialog, setIsConfirmationDialog] = useState(true);
  const [editAccess, setEditAccess] = useState(false);
  const [expanded, setExpanded] = useState("panel1");
  const [nameFilterAnchorEl, setNameFilterAnchorEl] = useState(null);
  const [nameFilterOperator, setNameFilterOperator] = useState("contains");
  const [nameFilterValue, setNameFilterValue] = useState("");
  const [statusFilterAnchorEl, setStatusFilterAnchorEl] = useState(null);
  const [statusFilterOperator, setStatusFilterOperator] = useState("contains");
  const [statusFilterValue, setStatusFilterValue] = useState("");
  const [tempDateValues, setTempDateValues] = useState({});
  const [searchResults, setSearchResults] = useState([]);
  const [searchCriteria, setSearchCriteria] = useState("");
  const [comments, setComments] = useState("");
  const [taskTypeWarningOpen, setTaskTypeWarningOpen] = useState(false);
  const [pendingTaskType, setPendingTaskType] = useState(null);
  const [historyOpen, sethistoryOpen] = useState(false);
  const [historyData, sethistoryData] = useState([]);
  const [historyLoading, sethistoryLoading] = useState(false);
  const [historyRow, sethistoryRow] = useState(null);
  const [ridSortOrder, setRidSortOrder] = useState(null);
  const [lastSearchClassName, setLastSearchClassName] = React.useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [isEnableUpload, setIsEnableUpload] = useState(false);
  const [classMode, setClassMode] = useState("roster");
  const [uploadClassOptions, setUploadClassOptions] = useState([]);
  const [selectedUploadClass, setSelectedUploadClass] = useState([]);

  const isNoRosterClass = useMemo(() => {
    return (
      rows.length > 0 &&
      rows.every(
        (r) => r.ClassRosterId === null || r.ClassRosterId === undefined,
      )
    );
  }, [rows]);
  const techOptionsRef = useRef([]);
  const currentUser = localStorage.getItem("User");

  const handleChangePanel = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const getFieldsToNullify = (newTaskType) => {
    const fieldNameMapping = {
      EXT: "EXT",
      EXTScheduleStart: "EXT Schedule Start",
      EXTScheduleEnd: "EXT Schedule End",
      EXTScheduleStatus: "EXT Schedule Status",
      PERR: "PERR",
      PERRScheduleStart: "PERR Schedule Start",
      PERRScheduleEnd: "PERR Schedule End",
      PERRScheduleStatus: "PERR Schedule Status",
      Recovery: "Recovery",
      RecoveryScheduleStart: "Recovery Schedule Start",
      RecoveryScheduleEnd: "Recovery Schedule End",
      RecoveryScheduleStatus: "Recovery Schedule Status",
    };

    const allFields = [
      "EXT",
      "EXTScheduleStart",
      "EXTScheduleEnd",
      "EXTScheduleStatus",
      "PERR",
      "PERRScheduleStart",
      "PERRScheduleEnd",
      "PERRScheduleStatus",
      "Recovery",
      "RecoveryScheduleStart",
      "RecoveryScheduleEnd",
      "RecoveryScheduleStatus",
    ];

    const newVisibleFields = getVisibleFieldsForTaskType(newTaskType);
    const fieldsToRemove = allFields.filter(
      (field) => !newVisibleFields.includes(field),
    );

    return fieldsToRemove.map((field) => fieldNameMapping[field]);
  };

  const handleBulkEditWithWarning = (field, value) => {
    if (field === "TaskTypeDescription" && value) {
      const fieldsToNullify = getFieldsToNullify(value.TaskTypeDescription);

      if (fieldsToNullify.length > 0) {
        setPendingTaskType({ field, value });
        setTaskTypeWarningOpen(true);
        return;
      }
    }

    handleBulkEdit(field, value);

    if (field === "TaskTypeDescription" && value?.OMTTaskTypeId) {
      setSelectedTaskTypeId(value.OMTTaskTypeId);
    }
  };

  const confirmTaskTypeChange = () => {
    if (pendingTaskType) {
      handleBulkEdit(pendingTaskType.field, pendingTaskType.value);
      if (pendingTaskType.value.OMTTaskTypeId) {
        setSelectedTaskTypeId(pendingTaskType.value.OMTTaskTypeId);
      }
      setPendingTaskType(null);
    }
    setTaskTypeWarningOpen(false);
  };

  const handleHistoryClick = async (e, row) => {
    e.stopPropagation();
    sethistoryRow(row);
    sethistoryLoading(true);
    sethistoryOpen(true);

    try {
      const response = await getOMTClassDetailsHistory(row.OMTClassDetailsId);
      const historyData = extractHistoryData(response);

      if (historyData && historyData.length > 0) {
        handleSuccessfulHistory(historyData, sethistoryData, setSnack);
      } else {
        handleEmptyHistory(sethistoryData, setSnack);
      }
    } catch (error) {
      handleHistoryError(error, sethistoryData, setSnack);
    } finally {
      sethistoryLoading(false);
    }
  };

  const handleHistoryClose = () => {
    sethistoryOpen(false);
    sethistoryData([]);
    sethistoryRow(null);
  };

  const historyColumns = [
    {
      field: "Column",
      header: "Column Name",
      sortable: true,
      align: "center",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "value",
      header: "Change Value",
      sortable: true,
      align: "center",
      bodyStyle: { textAlign: "left" },
      body: (rowData) => (
        <Typography
          variant="body2"
          sx={{
            maxWidth: "500px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={rowData.value || "null"}
        >
          {rowData.value || "null"}
        </Typography>
      ),
    },
    {
      field: "ModifiedBy",
      header: "Modified By",
      sortable: true,
      align: "center",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "ModifiedDate",
      header: "Modified Date",
      sortable: true,
      align: "center",
      bodyStyle: { textAlign: "center" },
      body: (rowData) =>
        rowData.ModifiedDate
          ? new Date(rowData.ModifiedDate).toLocaleString()
          : "N/A",
    },
  ];

  useEffect(() => {
    const fetchConfigValue = async () => {
      try {
        const response = await getConfigurationValues(
          "appsettings:UploadNoRoster",
        );

        if (response?.statusCode === 200) {
          setIsEnableUpload(response?.data?.UploadNoRoster?.toString() === "1");
        }
      } catch (error) {
        console.error("Failed to fetch config value:", error);
      }
    };

    fetchConfigValue();
    const validateAccess = () => {
      localStorage.setItem("url", window.location.pathname);
      setEditAccess(hasPermission("ASD_Group"));
      window.localStorage.removeItem("url");
    };

    validateAccess();

    applicationUsageLog(
      localStorage.getItem("User"),
      localStorage.getItem("EmailId"),
      "obtinfo - OMT",
      "Class Setup",
    );
  }, [hasPermission]);

  const handleDialogOpen = (
    action,
    title,
    message,
    messageType,
    dialogBulletList,
    isConfirmation,
  ) => {
    setDialogAction(() => action);
    setDialogTitle(title);
    setDialogMessage(message);
    setDialogMessageType(messageType);
    setDialogBulletList(dialogBulletList);
    setIsConfirmationDialog(isConfirmation);
    setDialogOpen(true);
  };

  useEffect(() => {
    setRows((prevRows) => [...prevRows]);
  }, [savedCells, editedCells]);

  useEffect(() => {
    if (selectedRows.length === 0) {
      setHeaderResetTrigger((prev) => !prev);
    }
  }, [selectedRows]);

  useEffect(() => {
    let cancelled = false;

    const loadTaskAndStatusOptions = async () => {
      try {
        const [taskRes, statusRes] = await Promise.all([
          getAllTaskTypes(),
          getAllStatus(),
        ]);

        if (cancelled) return;

        if (!cancelled) {
          setTaskOptions(taskRes.data?.data || []);
          setStatusOptions(statusRes.data?.data || []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load task/status options:", error);
        }
      }
    };

    loadTaskAndStatusOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadOptions = async () => {
      if (!cancelled) {
        setLoading(true);
      }

      try {
        const response = await getAllTechSolutions();

        if (cancelled) return;

        if (
          response?.data?.message === "Success" &&
          Array.isArray(response.data.data)
        ) {
          if (!cancelled) {
            techOptionsRef.current = response.data.data;
            setTechOptions(response.data.data);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setSnack({
            open: true,
            message: "Failed to load tech options.",
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleClear = () => {
    setRows([]);
    setParentClient(null);
    setWave(null);
    setProject(null);
    setRosterValue([]);
    setSelectedRows([]);
    setEditedCells({});
    setSavedCells({});
    setHeaderResetTrigger((prev) => !prev);
    setPage(0);
    setNameFilterValue("");
    setNameFilterOperator("contains");
    setStatusFilterValue("");
    setStatusFilterOperator("contains");
    setSearchCriteria("");
    setSearchResults([]);
    setSelectedUploadClass([]);
  };

  const handleToggleChange = async (event) => {
    const isUpload = event.target.checked;
    setClassMode(isUpload ? "upload" : "roster");
    handleClear();
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const parentClientResponse = await getAllParentClients();
      if (parentClientResponse?.data?.message === "Success") {
        setParentClientOptions(parentClientResponse?.data?.data);
      } else {
        errorHandler(true, "No Parent Clients Found...", "error");
      }

      if (classMode === "upload") {
        const noRosterResponse = await getAllNoRosterIdClasses();
        if (noRosterResponse?.data?.message === "Success") {
          setUploadClassOptions(noRosterResponse?.data?.data);
        } else {
          errorHandler(true, "No OMT Roster Data Found...", "error");
        }
      } else {
        const classRosterResponse = await getClassRosters();
        if (classRosterResponse?.data?.message === "Success") {
          setFilterRosterData(classRosterResponse?.data?.data);
        } else {
          errorHandler(true, "No Roster Data Found...", "error");
        }
      }
    } catch {
      errorHandler(true, "Some internal error occured.", "error");
    }
    setLoading(false);
  };

  const fetchClassRosters = async (ProjectCode, wave) => {
    setLoading(true);
    try {
      const response = await getClassRosters(ProjectCode, wave);
      if (response?.data?.message === "Success") {
        setFilterRosterData(response.data.data);
      } else {
        errorHandler(true, "ClassRosters could not be fetched", "error");
      }
    } catch {
      errorHandler(true, "Some internal error occured", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    let cancelled = false;

    const loadClassRosters = async () => {
      try {
        if (project || wave) {
          await fetchClassRosters(
            project?.ProjectCode ?? null,
            wave?.Wave ?? null,
          );
        } else {
          await fetchClassRosters();
        }

        if (!cancelled) {
          setRosterValue([]);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch class rosters:", error);
        }
      }
    };

    loadClassRosters();

    return () => {
      cancelled = true;
    };
  }, [project, wave]);

  const handleParentClientChange = async (event, value) => {
    setLoading(true);
    setParentClient(value);
    setProject({ ProjectName: "", ProjectCode: "" });
    setWave({ Wave: "" });
    try {
      if (value !== null) {
        const clientResponse = await getAllProjectsByParentClientCode(
          value?.ParentClientCode,
        );
        if (clientResponse?.data?.message === "Success") {
          setProjectOptions(clientResponse?.data?.data);
          setWaveOptions([]);
        } else {
          errorHandler(true, "No data Found..", "error");
        }
      } else {
        setProjectOptions([]);
        setWaveOptions([]);
      }
    } catch {
      errorHandler(true, "Some internal error occured.", "error");
    }
    setLoading(false);
  };

  const handleProjectChange = async (event, value) => {
    setLoading(true);
    setProject(value);
    setWave({ Wave: "" });
    try {
      if (value !== null) {
        const waveResponse = await getWaveDetails(value?.ProjectCode);
        if (waveResponse?.data?.message === "Success") {
          setWaveOptions(waveResponse?.data?.data);
        } else {
          errorHandler(true, "Waves could not be fetched.", "error");
        }
      } else {
        setWaveOptions([]);
      }
    } catch {
      errorHandler(true, "Some internal error occured.", "error");
    }
    setLoading(false);
  };

  const handleSearch = async (className = null) => {
    if (
      (!rosterValue || rosterValue.length === 0) &&
      (!project?.ProjectCode || !wave?.Wave) &&
      !className
    ) {
      errorHandler(
        true,
        "Please select either Class Roster or ParentClient + Project + Wave",
        "error",
      );
      return;
    }
    setLoading(true);
    setSearchCriteria("");
    setSearchResults([]);
    if (className) setLastSearchClassName(className);
    const payload = {
      projectCode: project?.ProjectCode || "",
      wave: wave?.Wave || "",
      classRosterId:
        rosterValue?.length > 0
          ? rosterValue.map((r) => r.ClassRosterId).join(",")
          : "",
    };
    try {
      const response = await getClassSearchResults(
        payload.projectCode,
        payload.wave,
        payload.classRosterId,
        className,
      );
      if (response?.data?.message === "Success") {
        const findIdByDescription = (options, description, descKey, idKey) => {
          if (!description || !Array.isArray(options)) return null;
          const found = options.find((opt) => opt[descKey] === description);
          return found?.[idKey] || null;
        };

        const formatted = response.data.data.map((row) => ({
          ...row,
          id: className ? row.OMTClassDetailsId : row.ClassTraineeId,
          NoRosterIdClassName: className || row.NoRosterIdClassName || null,
          ASDLeadOracleId: row.ASDLead || "",
          ASDLeadEmail: row.ASDLeadEmail || "",
          IsScheduleCancelled: row.IsScheduleCancelled || false,
          IsEXTScheduleCancelled: row.IsEXTScheduleCancelled || false,
          IsPERRScheduleCancelled: row.IsPERRScheduleCancelled || false,
          IsRecoveryScheduleCancelled: row.IsRecoveryScheduleCancelled || false,
          OMTBuildId:
            row.OMTBuildId ||
            findIdByDescription(
              buildOptions,
              row.BuildDescription,
              "BuildDescription",
              "OMTBuildId",
            ),
          OMTTechSolutionId:
            row.OMTTechSolutionId ||
            findIdByDescription(
              techOptions,
              row.TechSolutionDescription,
              "TechSolutionDescription",
              "OMTTechSolutionId",
            ),
          OMTTaskTypeId:
            row.OMTTaskTypeId ||
            findIdByDescription(
              taskOptions,
              row.TaskTypeDescription,
              "TaskTypeDescription",
              "OMTTaskTypeId",
            ),
          OMTFinalStatusId:
            row.OMTFinalStatusId ||
            findIdByDescription(
              statusOptions,
              row.FinalStatusDescription,
              "FinalStatusDescription",
              "OMTFinalStatusId",
            ),
        }));

        const uniqueTechSolutionIds = [
          ...new Set(
            formatted
              .filter((row) => row.OMTTechSolutionId)
              .map((row) => row.OMTTechSolutionId),
          ),
        ];

        if (uniqueTechSolutionIds.length === 1) {
          try {
            const buildResponse = await getBulkReBuild(
              uniqueTechSolutionIds[0],
            );
            if (
              buildResponse?.data?.message === "Success" &&
              Array.isArray(buildResponse.data.data)
            ) {
              setBuildOptions(buildResponse.data.data);

              const updatedFormatted = formatted.map((row) => ({
                ...row,
                OMTBuildId:
                  row.OMTBuildId ||
                  findIdByDescription(
                    buildResponse.data.data,
                    row.BuildDescription,
                    "BuildDescription",
                    "OMTBuildId",
                  ),
              }));

              setRows(updatedFormatted);
              setExpanded("panel2");
              setPage(0);
              setLoading(false);
              return;
            }
          } catch (err) {
            console.error("Error loading build options:", err);
          }
        }

        setRows(formatted);
        setExpanded("panel2");
        setPage(0);
      } else {
        setRows([]);
        errorHandler(true, "No Data Found", "error");
      }
    } catch {
      setRows([]);
      errorHandler(true, "Something went wrong", "error");
    }
    setLoading(false);
  };

  const handleNoRosterIdSearch = async (className = null) => {
    if (!selectedUploadClass || selectedUploadClass.length === 0) {
      setRows([]);
      setSnack({
        open: true,
        message: "Please select a class name to continue.",
        severity: "warning",
      });
      return;
    }

    const classNames = selectedUploadClass
      .map((c) => c?.NoRosterIdClassName)
      .filter(Boolean)
      .join(",");

    setLoading(true);
    await handleUploadSuccess(classNames, true);
    setLoading(false);
  };

  const handleUploadSuccess = async (className, isRosterIdSearch = false) => {
    setUploadModalOpen(false);
    setExpanded("panel2");
    setLoading(true);
    setRows([]);

    if (!isRosterIdSearch) {
      try {
        const noRosterResponse = await getAllNoRosterIdClasses();
        if (noRosterResponse?.data?.message === "Success") {
          setUploadClassOptions(noRosterResponse?.data?.data);
        }
      } catch (err) {
        console.error("Failed to refresh uploaded class options:", err);
      }
    }
    try {
      const response = await getClassSearchResults("", "", "", className);
      if (
        response?.data?.message === "Success" &&
        response?.data?.data?.length > 0
      ) {
        const formatted = response.data.data
          .map((row) => ({
            ...row,
            id: row.OMTClassDetailsId,
            NoRosterIdClassName: row.NoRosterIdClassName ?? className,
            ASDLeadOracleId: row.ASDLead || "",
            ASDLeadEmail: row.ASDLeadEmail || "",
            IsScheduleCancelled: row.IsScheduleCancelled || false,
            IsEXTScheduleCancelled: row.IsEXTScheduleCancelled || false,
            IsPERRScheduleCancelled: row.IsPERRScheduleCancelled || false,
            IsRecoveryScheduleCancelled:
              row.IsRecoveryScheduleCancelled || false,
            ScheduleStart: row.ScheduleStart || null,
            ScheduleEnd: row.ScheduleEnd || null,
            OMTTechSolutionId:
              row.OMTTechSolutionId ||
              techOptions.find(
                (o) =>
                  o.TechSolutionDescription === row.TechSolutionDescription,
              )?.OMTTechSolutionId ||
              null,
            OMTBuildId:
              row.OMTBuildId ||
              buildOptions.find(
                (o) => o.BuildDescription === row.BuildDescription,
              )?.OMTBuildId ||
              null,
            OMTTaskTypeId:
              row.OMTTaskTypeId ||
              taskOptions.find(
                (o) => o.TaskTypeDescription === row.TaskTypeDescription,
              )?.OMTTaskTypeId ||
              null,
            OMTFinalStatusId:
              row.OMTFinalStatusId ||
              statusOptions.find(
                (o) => o.FinalStatusDescription === row.FinalStatusDescription,
              )?.OMTFinalStatusId ||
              null,
          }))
          .sort((a, b) => {
            const classCompare = (a.NoRosterIdClassName || "").localeCompare(
              b.NoRosterIdClassName || "",
            );
            if (classCompare !== 0) return classCompare;
            return (a.EmployeeName || "").localeCompare(b.EmployeeName || "");
          });

        const uploadTechSolutionIds = [
          ...new Set(formatted.map((r) => r.OMTTechSolutionId).filter(Boolean)),
        ];
        const buildLookup = [];
        await Promise.all(
          uploadTechSolutionIds.map(async (techId) => {
            try {
              const buildResponse = await getBulkReBuild(techId);
              if (
                buildResponse?.data?.message === "Success" &&
                Array.isArray(buildResponse.data.data)
              ) {
                buildLookup.push(...buildResponse.data.data);
              }
            } catch (err) {
              console.error("Error loading build options:", err);
            }
          }),
        );
        const resolvedRows = formatted.map((row) => ({
          ...row,
          OMTBuildId:
            row.OMTBuildId ||
            buildLookup.find((o) => o.BuildDescription === row.BuildDescription)
              ?.OMTBuildId ||
            null,
        }));

        setRows(resolvedRows);
        setLastSearchClassName(className);
        setSnack({
          open: true,
          message: isRosterIdSearch
            ? "Uploaded Class details fetched successfully."
            : "Class created successfully!",
          severity: "success",
        });
      } else {
        setSnack({
          open: true,
          message: isRosterIdSearch
            ? "Uploaded Class details fetched successfully."
            : `Class created successfully. Search by class name to view it.`,
          severity: "success",
        });
      }
    } catch {
      setSnack({
        open: true,
        message: isRosterIdSearch
          ? "Try again to search Uploaded Class details."
          : `Class created successfully. Search by class name to view it.`,
        severity: "success",
      });
    }
    setLoading(false);
  };

  const errorHandler = (open, message, severity) => {
    setSnack({ open: true, message: message, severity: severity });
  };

  const snackReset = () => {
    setSnack({ open: false, message: "", severity: "" });
  };

  const handleNameFilterClick = (event) => {
    setNameFilterAnchorEl(event.currentTarget);
  };

  const handleNameFilterClose = () => {
    setNameFilterAnchorEl(null);
  };

  const handleApplyNameFilter = () => {
    setPage(0);
    handleNameFilterClose();
  };

  const handleClearNameFilter = () => {
    setNameFilterValue("");
    setNameFilterOperator("contains");
    setPage(0);
  };

  const handleStatusFilterClick = (event) => {
    setStatusFilterAnchorEl(event.currentTarget);
  };

  const handleStatusFilterClose = () => {
    setStatusFilterAnchorEl(null);
  };

  const handleApplyStatusFilter = () => {
    setPage(0);
    handleStatusFilterClose();
  };

  const handleClearStatusFilter = () => {
    setStatusFilterValue("");
    setStatusFilterOperator("contains");
    setPage(0);
  };

  const nameFilterOpen = Boolean(nameFilterAnchorEl);
  const statusFilterOpen = Boolean(statusFilterAnchorEl);

  const applyFilter = (row, filterValue, filterOperator, field) => {
    if (!filterValue) return true;

    const cellValue = row[field]?.toString().toLowerCase() || "";
    const searchValue = filterValue.toLowerCase();

    switch (filterOperator) {
      case "contains":
        return cellValue.includes(searchValue);
      case "equals":
        return cellValue === searchValue;
      case "startsWith":
        return cellValue.startsWith(searchValue);
      case "endsWith":
        return cellValue.endsWith(searchValue);
      default:
        return true;
    }
  };

  const filteredRows = useMemo(() => {
    let filtered = rows;

    if (nameFilterValue) {
      filtered = filtered.filter((row) =>
        applyFilter(row, nameFilterValue, nameFilterOperator, "EmployeeName"),
      );
    }

    if (statusFilterValue) {
      filtered = filtered.filter((row) =>
        applyFilter(
          row,
          statusFilterValue,
          statusFilterOperator,
          "TraineeStatus",
        ),
      );
    }

    if (ridSortOrder) {
      filtered = [...filtered].sort((a, b) => {
        const aVal = Number(a.ClassRosterId) || 0;
        const bVal = Number(b.ClassRosterId) || 0;
        return ridSortOrder === "asc" ? aVal - bVal : bVal - aVal;
      });
    }

    return filtered;
  }, [
    rows,
    nameFilterValue,
    nameFilterOperator,
    statusFilterValue,
    statusFilterOperator,
    ridSortOrder,
  ]);

  useEffect(() => {
    setPage(0);
  }, [
    nameFilterValue,
    nameFilterOperator,
    statusFilterValue,
    statusFilterOperator,
  ]);

  const displayedRows = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        await fetchData();

        if (!cancelled) {
          applicationUsageLog(
            localStorage.getItem("User"),
            localStorage.getItem("EmailId"),
            "obtinfo",
            "Class Setup",
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to initialize:", error);
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, [classMode]);

  useEffect(() => {
    const autocompleteFieldGroups = {
      TechSolutionDescription: ["TechSolutionDescription", "OMTTechSolutionId"],
      BuildDescription: ["BuildDescription", "OMTBuildId"],
      TaskTypeDescription: ["TaskTypeDescription", "OMTTaskTypeId"],
      FinalStatusDescription: ["FinalStatusDescription", "OMTFinalStatusId"],
      ASDLead: ["ASDLead", "ASDLeadEmail", "ASDLeadOracleId"],
    };

    let totalEditableCells = 0;

    Object.values(editedCells).forEach((cell) => {
      totalEditableCells += processCellKeys(cell, autocompleteFieldGroups);
    });

    setTotalUnsavedCells(totalEditableCells);
  }, [editedCells]);

  const getIdFromOptions = (
    options,
    description,
    descriptionKey,
    idKey,
    fallbackId,
  ) => {
    if (!Array.isArray(options) || options.length === 0)
      return fallbackId || null;
    const found = options.find((opt) => opt[descriptionKey] === description);
    return found?.[idKey] || fallbackId || null;
  };

  const transformToRequestBody = (rows) => {
    return rows.map((row) => ({
      omtClassDetailsId: row.OMTClassDetailsId ?? null,
      omtTechSolutionId:
        row.OMTTechSolutionId ??
        getIdFromOptions(
          techOptions,
          row.TechSolutionDescription,
          "TechSolutionDescription",
          "OMTTechSolutionId",
          null,
        ),
      omtBuildId:
        row.OMTBuildId ??
        getIdFromOptions(
          buildOptions,
          row.BuildDescription,
          "BuildDescription",
          "OMTBuildId",
          null,
        ),
      omtTaskTypeId:
        row.OMTTaskTypeId ??
        getIdFromOptions(
          taskOptions,
          row.TaskTypeDescription,
          "TaskTypeDescription",
          "OMTTaskTypeId",
          null,
        ),
      omtFinalStatusId:
        row.OMTFinalStatusId ??
        getIdFromOptions(
          statusOptions,
          row.FinalStatusDescription,
          "FinalStatusDescription",
          "OMTFinalStatusId",
          null,
        ),
      oracleId: row.OracleId || "",
      classRosterId: row.ClassRosterId || null,
      classTraineeId: row.ClassTraineeId || null,
      asdLead: row.ASDLeadOracleId || row.ASDLead || "",
      scheduleStart: row.ScheduleStart || null,
      scheduleEnd: row.ScheduleEnd || null,
      omtScheduleStatusId: getIdFromOptions(
        statusOptions,
        row.ScheduleStatus,
        "FinalStatusDescription",
        "OMTFinalStatusId",
        row.OMTScheduleStatusId,
      ),
      ext: !!row.EXT,
      extScheduleStart: row.EXTScheduleStart || null,
      extScheduleEnd: row.EXTScheduleEnd || null,
      omtEXTScheduleStatusId: getIdFromOptions(
        statusOptions,
        row.EXTScheduleStatus,
        "FinalStatusDescription",
        "OMTFinalStatusId",
        row.OMTEXTScheduleStatusId,
      ),
      perr: !!row.PERR,
      perrScheduleStart: row.PERRScheduleStart || null,
      perrScheduleEnd: row.PERRScheduleEnd || null,
      omtPERRScheduleStatusId: getIdFromOptions(
        statusOptions,
        row.PERRScheduleStatus,
        "FinalStatusDescription",
        "OMTFinalStatusId",
        row.OMTPERRScheduleStatusId,
      ),
      recovery: !!row.Recovery,
      recoveryScheduleStart: row.RecoveryScheduleStart || null,
      recoveryScheduleEnd: row.RecoveryScheduleEnd || null,
      omtRecoveryScheduleStatusId: getIdFromOptions(
        statusOptions,
        row.RecoveryScheduleStatus,
        "FinalStatusDescription",
        "OMTFinalStatusId",
        row.OMTRecoveryScheduleStatusId,
      ),
      ticketNumber: row.TicketNumber || null,
      comments: row.Comments || null,
      modifiedBy: currentUser,
      noRosterIdClassName:
        row.NoRosterIdClassName || row.noRosterIdClassName || null,
      isExcelUpload: false,
    }));
  };

  const hasInvalidCells = Object.values(invalidCells).some((row) =>
    Object.values(row).some((isInvalid) => isInvalid),
  );

  const handleSaveData = async () => {
    setLoading(true);
    try {
      if (totalUnsavedCells > 0) {
        const saveDataDialog = {
          title: "Save Data",
          message:
            "Are you sure you want to save the bulk changes for OMT Class Details?",
          messageType: "Confirmation",
          details: [],
          isConfirmation: true,
        };

        handleDialogOpen(
          async () => {
            setLoading(true);
            try {
              const processedRows = rows.map((row) =>
                processRowForSave(row, editedCells, selectedRows, getRowId),
              );

              const rowToSave = transformToRequestBody(processedRows).filter(
                (item, index) => {
                  const originalRow = processedRows[index];
                  return selectedRows.includes(getRowId(originalRow));
                },
              );

              if (!rowToSave || rowToSave.length === 0) {
                setSnack({
                  open: true,
                  message:
                    "No rows to save. Please select rows and make changes.",
                  severity: "warning",
                });
                setLoading(false);
                return;
              }

              const sanitizedRowToSave = rowToSave.map((row) => {
                const clean = {};
                Object.keys(row).forEach((key) => {
                  try {
                    JSON.stringify(row[key]);
                    clean[key] = row[key];
                  } catch (e) {
                    console.warn(
                      "Skipping circular field:",
                      key,
                      typeof row[key],
                    );
                  }
                });
                return clean;
              });

              const response = await saveOMTClassDetails(sanitizedRowToSave);

              if (response?.statusCode === 200) {
                setRows(processedRows);
                setSavedCells({ ...editedCells });
                setTimeout(() => {
                  setSavedCells({});
                  setEditedCells({});
                  setInvalidCells({});
                }, 1500);
                setSnack({
                  open: true,
                  message: "All changes have been saved successfully.",
                  severity: "success",
                });
              } else {
                setSnack({
                  open: true,
                  message: "Failed to Save",
                  severity: "error",
                });
              }
            } catch (error) {
              setSnack({
                open: true,
                message: "Failed to Save",
                severity: "error",
              });
            } finally {
              setLoading(false);
            }
          },
          saveDataDialog.title,
          saveDataDialog.message,
          saveDataDialog.messageType,
          saveDataDialog.details,
          saveDataDialog.isConfirmation,
        );
      } else {
        setSnack({
          open: true,
          message: "No unsaved changes detected.",
          severity: "info",
        });
      }
    } catch (err) {
      console.log(`Exception while doing Saving: ${err}`);
      setSnack({
        open: true,
        message: "An error occurred while saving data.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEdit = (field, value) => {
    if (selectedRows.length === 0) {
      setSnack({
        open: true,
        message: "No rows selected for bulk edit.",
        severity: "error",
      });
      return;
    }

    let hasInvalidDates = false;
    const invalidRowIds = [];

    const updatedRows = rows.map((row) => {
      if (!selectedRows.includes(getRowId(row))) return row;

      const result = processRowForBulkEdit(
        row,
        selectedRows,
        field,
        value,
        setInvalidCells,
        setSnack,
        getRowId,
      );

      if (result.hasInvalidDates) {
        hasInvalidDates = true;
        invalidRowIds.push(result.invalidRowId);
      }

      const updatedRow = result.updatedRow || row;

      setEditedCells((prev) => ({
        ...prev,
        [getRowId(row)]: {
          ...(prev[getRowId(row)] || {}),
          [field]: true,
        },
      }));

      return updatedRow;
    });

    if (hasInvalidDates) {
      setSnack({
        open: true,
        message: `${invalidRowIds.length} row(s) have invalid dates. Start date must be before end date and within 1 year.`,
        severity: "error",
      });
    }

    setRows(updatedRows);
  };

  const handleCancelButton = () => {
    const cancelButtongDialog =
      totalUnsavedCells > 0
        ? {
            title: "Cancel Bulk Edit",
            message:
              "You may lose the unsaved changes, Are you sure you want to cancel?",
            messageType: "Warning",
            details: [],
            isConfirmation: true,
          }
        : {
            title: "Cancel Bulk Edit",
            message: "Are you sure you want to cancel?",
            messageType: "Confirmation",
            details: [],
            isConfirmation: true,
          };

    handleDialogOpen(
      () => {
        setEditedCells({});
        setRows([]);
        setSelectedRows([]);
        setRosterValue([]);
        setParentClient(null);
        setWave(null);
        setProject(null);
        setPage(0);
        setNameFilterValue("");
        setNameFilterOperator("contains");
        setStatusFilterValue("");
        setStatusFilterOperator("contains");
      },
      cancelButtongDialog.title,
      cancelButtongDialog.message,
      cancelButtongDialog.messageType,
      cancelButtongDialog.details,
      cancelButtongDialog.isConfirmation,
    );
  };

  const onTechSolutionDescriptionChange = async (value) => {
    setLoading(true);
    try {
      const buildResponse = await getBulkReBuild(value?.OMTTechSolutionId);

      if (
        buildResponse?.data?.message === "Success" &&
        Array.isArray(buildResponse?.data.data) &&
        buildResponse.data.data.length > 0
      ) {
        setBuildOptions(buildResponse.data.data);
        return true;
      } else {
        setBuildOptions([]);
        errorHandler(
          true,
          "No Build Options Found for the Selected Tech Solution",
          "info",
        );
        return false;
      }
    } catch (err) {
      setBuildOptions([]);
      if (err?.response?.status === 404) {
        errorHandler(
          true,
          "No Build Options Found for the Selected Tech Solution",
          "info",
        );
      } else {
        console.log(`Exception while fetching: ${err}`);
        errorHandler(true, "Failed to fetch build options", "error");
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleDoNotDisturbClick = (rowId, scheduleType) => {
    const dialogConfig = {
      title: "Cancel Schedule",
      message: "Do you want to cancel the schedule?",
      messageType: "Confirmation",
      details: [],
      isConfirmation: true,
    };

    handleDialogOpen(
      async () => {
        setLoading(true);
        try {
          const row = rows.find((r) => r.id === rowId);
          if (!row) {
            setSnack({
              open: true,
              message: "Row did not found!!",
              severity: "error",
            });
            setLoading(false);
            return;
          }

          const requestBody = createCancelRequestBody(row, scheduleType);
          const response = await cancelOMTSchedule(
            JSON.stringify(requestBody),
            currentUser,
          );

          if (response?.statusCode === 200 || response?.message === "Success") {
            setSnack({
              open: true,
              message: "Schedule cancelled Successfully!!",
              severity: "success",
            });
            await handleSearch(lastSearchClassName);
          } else {
            setSnack({
              open: true,
              message: response?.message || "Failed to cancel schedule !!",
              severity: "error",
            });
          }
        } catch (error) {
          console.log(`Exception: ${error}`);
          setSnack({
            open: true,
            message: "Failed to cancel schedule!!",
            severity: "error",
          });
        }
        setLoading(false);
      },
      dialogConfig.title,
      dialogConfig.message,
      dialogConfig.messageType,
      dialogConfig.details,
      dialogConfig.isConfirmation,
    );
  };

  const handleRescheduleClick = (rowId, scheduleType) => {
    const dialogConfig = {
      title: "Reschedule Cancelled Schedule",
      message: "Do you want to reschedule this cancelled schedule?",
      messageType: "Confirmation",
      details: [],
      isConfirmation: true,
    };

    handleDialogOpen(
      async () => {
        setLoading(true);
        try {
          const row = rows.find((r) => r.id === rowId);
          if (!row) {
            setSnack({
              open: true,
              message: "Row did not found!!",
              severity: "error",
            });
            setLoading(false);
            return;
          }

          const requestBody = createRescheduleRequestBody(row, scheduleType);
          const response = await reschedulecancelledOMTSchedule(
            JSON.stringify(requestBody),
            currentUser,
          );

          if (response?.statusCode === 200 || response?.message === "Success") {
            setSnack({
              open: true,
              message: "Rescheduled Successfully!!",
              severity: "success",
            });

            setRows((prevRows) =>
              prevRows.map((r) => {
                if (r.id !== rowId) return r;
                return updateRowAfterReschedule(r, scheduleType);
              }),
            );
          } else {
            setSnack({
              open: true,
              message: response?.message || "Failed to reschedule!!",
              severity: "error",
            });
          }
        } catch (error) {
          console.log(`Exception: ${error}`);
          setSnack({
            open: true,
            message: "Failed to reschedule!!",
            severity: "error",
          });
        }
        setLoading(false);
      },
      dialogConfig.title,
      dialogConfig.message,
      dialogConfig.messageType,
      dialogConfig.details,
      dialogConfig.isConfirmation,
    );
  };

  const onPeopleSearch = async (event, searchQuery) => {
    if (searchQuery.length < 3) {
      setSnack({
        open: true,
        message: "Search criteria must be at least 3 characters in length.",
        severity: "error",
      });
      return;
    }
    setLoading(true);
    const searchResult = await searchEmployeeProfile(searchQuery);
    if (searchResult.status === true) {
      let searchResultDetails;
      if (/^\d+$/.test(searchQuery) && searchQuery.length >= 4) {
        searchResultDetails = searchResult.data.map((emp) => ({
          OracleId: emp.OracleId,
          Display:
            (emp.Name ?? "") + (emp.OracleId ? ` (${emp.OracleId})` : ""),
          Name: emp.Name,
          Email: emp.Email,
        }));
      } else {
        searchResultDetails = searchResult.data.map((emp) => ({
          OracleId: emp.OracleId,
          Display: emp.Display,
          Name: emp.Name,
          Email: emp.Email,
        }));
      }
      setSearchResults(searchResultDetails);
      setLoading(false);
      setSnack({
        open: true,
        message: "We found results matching the search criteria.",
        severity: "success",
      });
    } else {
      setSnack({
        open: true,
        message: "No result found matching with search criteria.",
        severity: "error",
      });
    }
  };

  const handleLoadOMT = () => {
    if (classMode === "upload") {
      const dashboardUrl = `https://askted.ttec.com/dashboards/uRBZ6IcbelK658nhsAMN57?Class+Roster+ID=${selectedUploadClass?.NoRosterIdClassName}`;
      window.open(dashboardUrl, "_blank");
    } else {
      if (!rosterValue || !rosterValue.length === 0) {
        setSnack({
          open: true,
          message: "Please select a Class Roster before loading OMT dashboard.",
          severity: "warning",
        });
        return;
      }
      rosterValue.forEach((roster) => {
        const dashboardUrl = `https://askted.ttec.com/dashboards/uRBZ6IcbelK658nhsAMN57?Class+Roster+ID=${roster.ClassRosterId}`;
        window.open(dashboardUrl, "_blank");
      });
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = filteredRows.map((row) => getRowId(row));
      setSelectedRows(newSelecteds);
      return;
    }
    setSelectedRows([]);
  };

  const handleRowClick = (event, row) => {
    const id = getRowId(row);
    const selectedIndex = selectedRows.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRows, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1));
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedRows.slice(0, selectedIndex),
        selectedRows.slice(selectedIndex + 1),
      );
    }

    setSelectedRows(newSelected);
  };

  const getRowId = (row) =>
    isNoRosterClass ? row.OMTClassDetailsId : row.ClassTraineeId;
  const isSelected = (id) => selectedRows.indexOf(id) !== -1;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCellClick = (rowId, field, row) => {
    if (selectedRows.includes(row ? getRowId(row) : rowId) && editAccess) {
      setEditingCell({ rowId, field });
      const currentRow = rows.find((r) => getRowId(r) === rowId);
      setComments(currentRow?.[field] || "");
    }
  };

  const handleCellDoubleClick = (rowId, field, row) => {
    if (selectedRows.includes(row ? getRowId(row) : rowId) && editAccess) {
      setEditingCell({ rowId, field });
      const currentRow = rows.find((r) => getRowId(r) === rowId);
      setComments(currentRow?.[field] || "");
    }
  };

  const handleCellUpdate = (rowId, field, value) => {
    const currentRow = rows.find((r) => getRowId(r) === rowId);

    validateAndUpdateDateCell(
      field,
      value,
      currentRow,
      rowId,
      setInvalidCells,
      setSnack,
    );

    setRows((prevRows) =>
      prevRows.map((row) =>
        getRowId(row) === rowId ? { ...row, [field]: value } : row,
      ),
    );

    setEditedCells((prev) => ({
      ...prev,
      [rowId]: {
        ...(prev[rowId] || {}),
        [field]: true,
      },
    }));

    setEditingCell({ rowId: null, field: null });
  };

  const getCellClassName = (rowId, field) => {
    if (invalidCells[rowId]?.[field]) return "invalid-cell";
    if (savedCells[rowId]?.[field]) return "saved";
    if (editedCells[rowId]?.[field]) return "unsaved";
    return "";
  };

  const selectedTaskType = useMemo(() => {
    const match = taskOptions.find(
      (opt) => opt.OMTTaskTypeId === selectedTaskTypeId,
    );
    return match?.TaskTypeDescription?.trim() || "";
  }, [selectedTaskTypeId, taskOptions]);

  const pcWrapperFields = [
    "ClassRosterId",
    "EmployeeName",
    "TraineeStatus",
    "ASDLead",
    "TechSolutionDescription",
    "BuildDescription",
    "TaskTypeDescription",
    "ScheduleStart",
    "ScheduleEnd",
    "ScheduleStatus",
    "ScheduleFailureReason",
    "TicketNumber",
    "FinalStatusDescription",
    "Comments",
  ];

  const tciFields = [
    "ClassRosterId",
    "EmployeeName",
    "TraineeStatus",
    "ASDLead",
    "TechSolutionDescription",
    "BuildDescription",
    "TaskTypeDescription",
    "ScheduleStart",
    "ScheduleEnd",
    "ScheduleStatus",
    "ScheduleFailureReason",
    "EXT",
    "EXTScheduleStart",
    "EXTScheduleEnd",
    "EXTScheduleStatus",
    "EXTScheduleFailureReason",
    "PERR",
    "PERRScheduleStart",
    "PERRScheduleEnd",
    "PERRScheduleStatus",
    "PERRScheduleFailureReason",
    "TicketNumber",
    "FinalStatusDescription",
    "Comments",
  ];

  const ttdFields = [
    "ClassRosterId",
    "EmployeeName",
    "TraineeStatus",
    "ASDLead",
    "TechSolutionDescription",
    "BuildDescription",
    "TaskTypeDescription",
    "ScheduleStart",
    "ScheduleEnd",
    "ScheduleStatus",
    "ScheduleFailureReason",
    "Recovery",
    "RecoveryScheduleStart",
    "RecoveryScheduleEnd",
    "RecoveryScheduleStatus",
    "RecoveryScheduleFailureReason",
    "TicketNumber",
    "FinalStatusDescription",
    "Comments",
  ];

  const visibleFields = useMemo(() => {
    if (selectedTaskType === "PC wrapper" || selectedTaskType === "Migration")
      return pcWrapperFields;
    if (selectedTaskType === "TCI" || selectedTaskType === "ORT")
      return tciFields;
    if (selectedTaskType === "TTD") return ttdFields;

    return [
      "ClassRosterId",
      "EmployeeName",
      "TraineeStatus",
      "ASDLead",
      "TechSolutionDescription",
      "BuildDescription",
      "TaskTypeDescription",
      "ScheduleStart",
      "ScheduleEnd",
      "ScheduleStatus",
      "ScheduleFailureReason",
      "EXT",
      "EXTScheduleStart",
      "EXTScheduleEnd",
      "EXTScheduleStatus",
      "EXTScheduleFailureReason",
      "PERR",
      "PERRScheduleStart",
      "PERRScheduleEnd",
      "PERRScheduleStatus",
      "PERRScheduleFailureReason",
      "Recovery",
      "RecoveryScheduleStart",
      "RecoveryScheduleEnd",
      "RecoveryScheduleStatus",
      "RecoveryScheduleFailureReason",
      "TicketNumber",
      "FinalStatusDescription",
      "Comments",
    ];
  }, [selectedTaskType]);

  const renderCell = (row, field) => {
    const rowId = isNoRosterClass ? row.OMTClassDetailsId : row.ClassTraineeId;
    const isEditing =
      editingCell.rowId === rowId && editingCell.field === field;
    const cellValue = row[field];

    if (field === "TaskTypeDescription") {
      return cellValue || "";
    }

    if (field === "ASDLead" && !isEditing) {
      return row.ASDLeadEmail || "";
    }

    if (!isEditing) {
      if (field === "ScheduleStart" || field === "ScheduleEnd") {
        return (
          <ScheduleDateCell
            row={row}
            field={field}
            cellValue={cellValue}
            scheduleType="schedule"
            editAccess={editAccess}
            handleRescheduleClick={handleRescheduleClick}
            handleDoNotDisturbClick={handleDoNotDisturbClick}
          />
        );
      }

      if (field === "EXTScheduleStart" || field === "EXTScheduleEnd") {
        return (
          <ScheduleDateCell
            row={row}
            field={field}
            cellValue={cellValue}
            scheduleType="ext"
            editAccess={editAccess}
            handleRescheduleClick={handleRescheduleClick}
            handleDoNotDisturbClick={handleDoNotDisturbClick}
          />
        );
      }

      if (field === "PERRScheduleStart" || field === "PERRScheduleEnd") {
        return (
          <ScheduleDateCell
            row={row}
            field={field}
            cellValue={cellValue}
            scheduleType="perr"
            editAccess={editAccess}
            handleRescheduleClick={handleRescheduleClick}
            handleDoNotDisturbClick={handleDoNotDisturbClick}
          />
        );
      }

      if (
        field === "RecoveryScheduleStart" ||
        field === "RecoveryScheduleEnd"
      ) {
        return (
          <ScheduleDateCell
            row={row}
            field={field}
            cellValue={cellValue}
            scheduleType="recovery"
            editAccess={editAccess}
            handleRescheduleClick={handleRescheduleClick}
            handleDoNotDisturbClick={handleDoNotDisturbClick}
          />
        );
      }

      if (field === "Comments") {
        return <CommentsCell cellValue={cellValue} />;
      }

      if (
        field === "ScheduleFailureReason" ||
        field === "EXTScheduleFailureReason" ||
        field === "PERRScheduleFailureReason" ||
        field === "RecoveryScheduleFailureReason"
      ) {
        return <CommentsCell cellValue={cellValue} />;
      }

      if (field === "EXT" || field === "PERR" || field === "Recovery") {
        return <CheckboxCell cellValue={cellValue} />;
      }

      return cellValue || "";
    }

    // Editing mode
    if (field === "Comments") {
      return (
        <CommentsEditCell
          row={row}
          rowId={rowId}
          field={field}
          comments={comments}
          setComments={setComments}
          handleCellUpdate={handleCellUpdate}
          setEditingCell={setEditingCell}
        />
      );
    }

    if (field === "TicketNumber") {
      return (
        <TicketNumberEditCell
          rowId={rowId}
          field={field}
          cellValue={cellValue}
          handleCellUpdate={handleCellUpdate}
          setEditingCell={setEditingCell}
        />
      );
    }

    if (
      field === "ScheduleFailureReason" ||
      field === "EXTScheduleFailureReason" ||
      field === "PERRScheduleFailureReason" ||
      field === "RecoveryScheduleFailureReason"
    ) {
      return <CommentsCell cellValue={cellValue} />;
    }

    if (field === "ASDLead") {
      return (
        <ASDLeadEditCell
          rowId={rowId}
          searchResults={searchResults}
          searchCriteria={searchCriteria}
          setSearchCriteria={setSearchCriteria}
          setEditingCell={setEditingCell}
          onPeopleSearch={onPeopleSearch}
          handleCellUpdate={handleCellUpdate}
          row={row}
          setSearchResults={setSearchResults}
        />
      );
    }

    if (field === "TechSolutionDescription") {
      return (
        <TechSolutionEditCell
          rowId={rowId}
          cellValue={cellValue}
          techOptions={techOptions}
          setEditingCell={setEditingCell}
          handleCellUpdate={handleCellUpdate}
          row={row}
          onTechSolutionDescriptionChange={onTechSolutionDescriptionChange}
        />
      );
    }

    if (field === "BuildDescription") {
      return (
        <BuildDescriptionCell
          row={row}
          buildOptions={buildOptions}
          setBuildOptions={setBuildOptions}
          onUpdate={(updates) => {
            Object.entries(updates).forEach(([key, value]) => {
              handleCellUpdate(rowId, key, value);
            });
          }}
        />
      );
    }

    if (
      field === "FinalStatusDescription" ||
      field === "ScheduleStatus" ||
      field === "EXTScheduleStatus" ||
      field === "PERRScheduleStatus" ||
      field === "RecoveryScheduleStatus"
    ) {
      return (
        <StatusEditCell
          rowId={rowId}
          field={field}
          cellValue={cellValue}
          statusOptions={statusOptions}
          setEditingCell={setEditingCell}
          handleCellUpdate={handleCellUpdate}
          row={row}
        />
      );
    }

    if (field.includes("Schedule")) {
      return (
        <ScheduleEditCell
          rowId={rowId}
          row={row}
          field={field}
          cellValue={cellValue}
          tempDateValues={tempDateValues}
          setTempDateValues={setTempDateValues}
          handleCellUpdate={handleCellUpdate}
          setEditingCell={setEditingCell}
          editingCell={editingCell}
        />
      );
    }

    if (field === "EXT" || field === "PERR" || field === "Recovery") {
      return (
        <CheckboxEditCell
          rowId={rowId}
          field={field}
          cellValue={cellValue}
          handleCellUpdate={handleCellUpdate}
        />
      );
    }

    return (
      <DefaultEditCell
        rowId={rowId}
        field={field}
        cellValue={cellValue}
        setEditingCell={setEditingCell}
        handleCellUpdate={handleCellUpdate}
      />
    );
  };

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <Card
        sx={{
          height: "calc(100vh - 80px)",
          width: "calc(100vw - 125px)",
          overflowY: "auto",
        }}
      >
        <Accordion
          expanded={expanded === "panel1"}
          onChange={handleChangePanel("panel1")}
        >
          {/* <AccordionSummary
            aria-controls="panel1bh-content"
            id="panel1bh-header"
            expandIcon={<ExpandMoreIcon />}
          >
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: "bold",
                fontFamily: "Urbanist, sans-serif",
                color: "#3F5765",
              }}
            >
              Class Setup
            </Typography>
          </AccordionSummary> */}
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <TextLabel
                text={
                  classMode === "upload"
                    ? "Uploaded Class Setup"
                    : "Class Roster Setup"
                }
                fontSize={16}
              />
              <Box
                sx={{
                  ml: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mr: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: classMode === "roster" ? 600 : 400,
                    color:
                      classMode === "roster"
                        ? "text.primary"
                        : "text.secondary",
                  }}
                >
                  Class Roster
                </Typography>
                <Switch
                  size="small"
                  checked={classMode === "upload"}
                  onChange={handleToggleChange}
                  color="primary"
                />
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: classMode === "upload" ? 600 : 400,
                    color:
                      classMode === "upload"
                        ? "text.primary"
                        : "text.secondary",
                  }}
                >
                  Uploaded Class
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <CardContent>
              {classMode === "upload" ? (
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12}>
                    <TextLabel text={"Uploaded Class"} />
                  </Grid>
                  <Grid item xs={3}>
                    <Autocomplete
                      size="small"
                      fullWidth
                      multiple
                      freeSolo
                      openOnFocus
                      disableCloseOnSelect
                      options={uploadClassOptions}
                      value={selectedUploadClass}
                      getOptionLabel={(item) =>
                        typeof item === "string"
                          ? item
                          : (item?.NoRosterIdClassName ?? "")
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.NoRosterIdClassName ===
                        value?.NoRosterIdClassName
                      }
                      onChange={(event, value) => {
                        setSelectedUploadClass(value);
                      }}
                      componentsProps={{
                        paper: {
                          sx: {
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          },
                        },
                      }}
                      renderTags={(value, getTagProps) => {
                        const limitTags = 1;
                        const more = value.length - limitTags;
                        const tooltipText = value
                          .map((v) =>
                            typeof v === "string" ? v : v.NoRosterIdClassName,
                          )
                          .join(", ");
                        return (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <Tooltip title={tooltipText} arrow>
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                {value
                                  .slice(0, limitTags)
                                  .map((option, index) => {
                                    const label =
                                      typeof option === "string"
                                        ? option
                                        : option.NoRosterIdClassName;
                                    return (
                                      <StyledChip
                                        key={label}
                                        label={label}
                                        size="small"
                                        {...getTagProps({ index })}
                                      />
                                    );
                                  })}
                              </span>
                            </Tooltip>
                            {more > 0 && (
                              <LimitChip label={`+${more}`} size="small" />
                            )}
                          </span>
                        );
                      }}
                      renderOption={(props, option) => {
                        const { key, ...optionProps } = props;
                        const label =
                          typeof option === "string"
                            ? option
                            : option.NoRosterIdClassName || "";
                        return (
                          <li
                            key={key}
                            {...optionProps}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "6px 12px",
                            }}
                          >
                            {label}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              maxHeight: 45,
                              flexWrap: "nowrap",
                              alignItems: "center",
                              paddingRight: "14px !important",
                              "& fieldset": { border: "d3d3d3" },
                            },
                            "& .MuiInputBase-input": {
                              minWidth: "60px !important",
                            },
                          }}
                          size="small"
                          placeholder={
                            selectedUploadClass?.length === 0
                              ? "Select or type class name"
                              : ""
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <CustomOutlinedButton
                      fullWidth
                      disabled={!isEnableUpload}
                      variant="outlined"
                      startIcon={<FileUploadOutlinedIcon />}
                      onClick={() => setUploadModalOpen(true)}
                    >
                      Upload class
                    </CustomOutlinedButton>
                  </Grid>
                  <Grid
                    item
                    xs={4}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "nowrap",
                      gap: "10px",
                    }}
                  >
                    <CustomButton
                      variant="contained"
                      onClick={handleNoRosterIdSearch}
                    >
                      Search
                    </CustomButton>
                    <CustomOutlinedButton
                      variant="outlined"
                      onClick={handleClear}
                    >
                      Clear
                    </CustomOutlinedButton>
                    <Tooltip
                      title={
                        !selectedUploadClass || selectedUploadClass.length === 0
                          ? "Select a Uploaded Class to enable OMT dashboard"
                          : `Load OMT dashboard for Roster ID: ${rosterValue.ClassRosterId}`
                      }
                      placement="top"
                    >
                      <span>
                        <CustomOutlinedButton
                          variant="outlined"
                          disabled={
                            !editAccess ||
                            !selectedUploadClass ||
                            selectedUploadClass.length === 0
                          }
                          onClick={handleLoadOMT}
                        >
                          <FileDownloadOutlinedIcon />
                          Load OMT
                        </CustomOutlinedButton>
                      </span>
                    </Tooltip>
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    sx={{ marginLeft: "11px" }}
                  >
                    <Grid item xs={2.2}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          fontFamily: "Urbanist, sans-serif",
                          color: "#3F5765",
                        }}
                      >
                        Parent Client
                      </Typography>
                    </Grid>
                    <Grid item xs={2.2}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          fontFamily: "Urbanist, sans-serif",
                          color: "#3F5765",
                        }}
                      >
                        Project
                      </Typography>
                    </Grid>
                    <Grid item xs={1.7}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          fontFamily: "Urbanist, sans-serif",
                          color: "#3F5765",
                        }}
                      >
                        Wave
                      </Typography>
                    </Grid>
                    <Grid item xs={2.5}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          fontFamily: "Urbanist, sans-serif",
                          color: "#3F5765",
                        }}
                      >
                        Class Roster
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    sx={{ marginLeft: "10px" }}
                  >
                    <Grid item xs={2}>
                      <Autocomplete
                        size="small"
                        disablePortal
                        blurOnSelect
                        options={parentClientOptions}
                        value={parentClient}
                        onChange={handleParentClientChange}
                        isOptionEqualToValue={(option, value) =>
                          value?.ParentClientName === "" ||
                          option?.ParentClientCode === value?.ParentClientCode
                        }
                        getOptionLabel={(option) =>
                          option?.ParentClientName === ""
                            ? ""
                            : `${
                                option?.ParentClientName +
                                " (" +
                                option?.ParentClientCode +
                                ")"
                              }`
                        }
                        renderInput={(params) => (
                          <TextField {...params} size="small" />
                        )}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={0.2}
                      sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center",
                        paddingBottom: 0.25,
                      }}
                    >
                      <ArrowCircleRightOutlinedIcon
                        sx={{ color: "#3299FF", height: 32, width: 32 }}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Autocomplete
                        size="small"
                        disablePortal
                        blurOnSelect
                        options={projectOptions}
                        value={project}
                        onChange={handleProjectChange}
                        isOptionEqualToValue={(option, value) =>
                          (value?.ProjectName === "" &&
                            value?.ProjectCode === "") ||
                          option?.ProjectCode === value?.ProjectCode
                        }
                        getOptionLabel={(option) =>
                          option?.ProjectName === "" &&
                          option?.ProjectCode === ""
                            ? ""
                            : `${
                                option?.ProjectName +
                                " (" +
                                option?.ProjectCode +
                                ")"
                              }`
                        }
                        renderInput={(params) => (
                          <TextField {...params} size="small" />
                        )}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={0.2}
                      sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center",
                        paddingBottom: 0.25,
                      }}
                    >
                      <ArrowCircleRightOutlinedIcon
                        sx={{ color: "#3299FF", height: 32, width: 32 }}
                      />
                    </Grid>
                    <Grid item xs={1.5}>
                      <Autocomplete
                        size="small"
                        disablePortal
                        blurOnSelect
                        options={waveOptions}
                        value={wave}
                        onChange={(e, inputValue) => setWave(inputValue)}
                        isOptionEqualToValue={(option, value) =>
                          value?.Wave === "" || option?.Wave === value?.Wave
                        }
                        getOptionLabel={(option) =>
                          option?.Wave === "" ? "" : `${option?.Wave}`
                        }
                        renderInput={(params) => (
                          <TextField {...params} size="small" />
                        )}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={0.2}
                      sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center",
                        paddingBottom: 0.25,
                      }}
                    >
                      <SwapHorizontalCircleOutlinedIcon
                        sx={{ color: "#3299FF", height: 32, width: 32 }}
                      />
                    </Grid>
                    <Grid item xs={2.3}>
                      <Autocomplete
                        size="small"
                        disablePortal
                        multiple
                        freeSolo
                        openOnFocus
                        disableCloseOnSelect
                        options={filterRosterData}
                        value={rosterValue}
                        getOptionDisabled={(option) =>
                          rosterValue.length >= 3 &&
                          !rosterValue.some(
                            (r) => r.ClassRosterId === option.ClassRosterId,
                          )
                        }
                        getOptionLabel={(option) =>
                          option?.ClassRosterId &&
                          option?.RosterStatus &&
                          option?.Wave
                            ? `${option?.ClassRosterId} - ${option?.RosterStatus} (${option?.Wave})`
                            : ""
                        }
                        isOptionEqualToValue={(option, value) =>
                          option?.ClassRosterId === value?.ClassRosterId
                        }
                        onChange={(event, value) => {
                          setRosterValue(value);
                        }}
                        renderTags={(value) => [
                          <Chip
                            key="tag"
                            label={
                              value[0]?.ClassRosterId &&
                              value[0]?.RosterStatus &&
                              value[0]?.Wave
                                ? `${value[0].ClassRosterId} - ${value[0].RosterStatus} (${value[0].Wave})`
                                : ""
                            }
                            size="small"
                            onDelete={() => {
                              setRosterValue(value.slice(1));
                            }}
                            sx={{
                              mr: 0.5,
                              backgroundColor: "#E3F2FD",
                              color: "#3299FF",
                              "& .MuiChip-deleteIcon": { color: "#3299FF" },
                            }}
                          />,
                          value.length > 1 && (
                            <CustomTooltip
                              key="more-tooltip"
                              title={value
                                .map((r) =>
                                  r?.ClassRosterId && r?.RosterStatus && r?.Wave
                                    ? `${r.ClassRosterId} - ${r.RosterStatus} (${r.Wave})`
                                    : "",
                                )
                                .join(", ")}
                            >
                              <Chip
                                key="more"
                                label={`+${value.length - 1}`}
                                size="small"
                                sx={{
                                  backgroundColor: "#3299FF",
                                  color: "white",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                }}
                              />
                            </CustomTooltip>
                          ),
                        ]}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder={
                              rosterValue?.length === 0
                                ? "RosterID-Status-Wave"
                                : ""
                            }
                            helperText={
                              rosterValue.length >= 3
                                ? "You can select upto 3 rosters only.."
                                : ""
                            }
                            FormHelperTextProps={{
                              sx: { color: "red", fontSize: "1rem" },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={3.5}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "nowrap",
                        gap: "10px",
                      }}
                    >
                      <CustomButton variant="contained" onClick={handleSearch}>
                        Search
                      </CustomButton>
                      <CustomOutlinedButton
                        variant="outlined"
                        onClick={handleClear}
                      >
                        Clear
                      </CustomOutlinedButton>
                      <Tooltip
                        title={
                          !rosterValue || rosterValue.length === 0
                            ? "Select a Class Roster to enable OMT dashboard"
                            : `Load OMT dashboard for Roster ID: ${rosterValue.ClassRosterId}`
                        }
                        placement="top"
                      >
                        <span>
                          <CustomOutlinedButton
                            variant="outlined"
                            disabled={
                              !editAccess ||
                              !rosterValue ||
                              rosterValue.length === 0
                            }
                            onClick={handleLoadOMT}
                          >
                            <FileDownloadOutlinedIcon />
                            Load OMT
                          </CustomOutlinedButton>
                        </span>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Grid>
              )}

              <Grid
                item
                xs={3.5}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "nowrap",
                  gap: "10px",
                }}
              ></Grid>
            </CardContent>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expanded === "panel2"}
          onChange={handleChangePanel("panel2")}
        >
          <AccordionDetails>
            <Grid item xs={12} sx={{ display: "flex", marginTop: "15px" }}>
              <Grid container spacing={2} item xs={12}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      width: "99%",
                      height: "calc(100vh - 225px)",
                      overflow: "auto",
                      marginLeft: "10px",
                    }}
                  >
                    <StyledTableContainer component={Paper}>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            {editAccess && (
                              <TableCell
                                padding="checkbox"
                                className="sticky-column"
                              >
                                <Checkbox
                                  indeterminate={
                                    selectedRows.length > 0 &&
                                    selectedRows.length < rows.length
                                  }
                                  checked={
                                    rows.length > 0 &&
                                    selectedRows.length === rows.length
                                  }
                                  onChange={handleSelectAllClick}
                                />
                              </TableCell>
                            )}
                            <TableCell
                              className={
                                editAccess
                                  ? "sticky-column-actions"
                                  : "sticky-column-actions-no-checkbox"
                              }
                            >
                              <Tooltip title="Actions" placement="top">
                                <span>Actions</span>
                              </Tooltip>
                            </TableCell>
                            {visibleFields.includes("ClassRosterId") && (
                              <TableCell
                                className={
                                  editAccess
                                    ? "sticky-column-rid"
                                    : "sticky-column-rid-no-checkbox"
                                }
                                onClick={() =>
                                  !isNoRosterClass &&
                                  setRidSortOrder((prev) =>
                                    prev === null
                                      ? "asc"
                                      : prev === "asc"
                                        ? "desc"
                                        : null,
                                  )
                                }
                                sx={{
                                  cursor: isNoRosterClass
                                    ? "default"
                                    : "pointer",
                                  userSelect: "none",
                                  minWidth: isNoRosterClass ? "160px" : "70px",
                                  width: isNoRosterClass ? "160px" : "70px",
                                }}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Tooltip
                                    title={
                                      isNoRosterClass || classMode === "upload"
                                        ? "Class Name"
                                        : "ClassRosterId"
                                    }
                                    placement="top"
                                  >
                                    <span>
                                      {isNoRosterClass || classMode === "upload"
                                        ? "Class Name"
                                        : "RID"}
                                    </span>
                                  </Tooltip>
                                  {!isNoRosterClass &&
                                    ridSortOrder === "asc" && (
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: "#3299FF",
                                        }}
                                      >
                                        ▲
                                      </span>
                                    )}
                                  {!isNoRosterClass &&
                                    ridSortOrder === "desc" && (
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: "#3299FF",
                                        }}
                                      >
                                        ▼
                                      </span>
                                    )}
                                  {!isNoRosterClass &&
                                    ridSortOrder === null && (
                                      <span
                                        style={{
                                          fontSize: "15px",
                                          color: "#aaa",
                                        }}
                                      >
                                        ⇅
                                      </span>
                                    )}
                                </Box>
                              </TableCell>
                            )}
                            {visibleFields.includes("EmployeeName") && (
                              <TableCell
                                className={
                                  editAccess
                                    ? `sticky-column-1${isNoRosterClass ? " no-roster-class" : ""}`
                                    : `sticky-column-1-no-checkbox${isNoRosterClass ? " no-roster-class" : ""}`
                                }
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Tooltip title="Name" placement="top">
                                    <span>Name</span>
                                  </Tooltip>
                                  <IconButton
                                    onClick={handleNameFilterClick}
                                    size="small"
                                    color={
                                      nameFilterValue ? "primary" : "default"
                                    }
                                  >
                                    <FilterListIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            )}
                            {visibleFields.includes("TraineeStatus") && (
                              <TableCell
                                className={
                                  editAccess
                                    ? `sticky-column-2${isNoRosterClass ? " no-roster-class" : ""}`
                                    : `sticky-column-2-no-checkbox${isNoRosterClass ? " no-roster-class" : ""}`
                                }
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Tooltip
                                    title={
                                      isNoRosterClass || classMode === "upload"
                                        ? "Employee Status"
                                        : "Trainee Status"
                                    }
                                    placement="top"
                                  >
                                    <span>
                                      {isNoRosterClass || classMode === "upload"
                                        ? "Employee Status"
                                        : "Trainee Status"}
                                    </span>
                                  </Tooltip>
                                  <IconButton
                                    onClick={handleStatusFilterClick}
                                    size="small"
                                    color={
                                      statusFilterValue ? "primary" : "default"
                                    }
                                  >
                                    <FilterListIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            )}
                            {visibleFields.includes("ASDLead") && (
                              <TableCell>
                                <CustomBulkOMTHeader
                                  headerTitle="ASD Lead"
                                  field="ASDLead"
                                  type="peoplePicker"
                                  onBulkEdit={handleBulkEdit}
                                  selectedRows={selectedRows}
                                  setSnack={setSnack}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes(
                              "TechSolutionDescription",
                            ) && (
                              <TableCell>
                                <CustomBulkOMTHeader
                                  headerTitle="Tech Solutions"
                                  field="TechSolutionDescription"
                                  type="autocomplete"
                                  headerTooltip="Select Tech Solutions"
                                  onBulkEdit={handleBulkEdit}
                                  selectedRows={selectedRows}
                                  setSnack={setSnack}
                                  setTechSolutionId={setSelectedTechSolutionId}
                                  options={techOptions}
                                  value={selectedTechSolutionId}
                                  onTechSolutionDescriptionChange={
                                    onTechSolutionDescriptionChange
                                  }
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes("BuildDescription") && (
                              <TableCell>
                                <CustomBulkOMTHeader
                                  headerTitle="Bulk Rebuild"
                                  field="BuildDescription"
                                  type="autocomplete"
                                  headerTooltip="Select Bulk Rebuild"
                                  setSnack={setSnack}
                                  options={buildOptions}
                                  selectedRows={selectedRows}
                                  onBulkEdit={handleBulkEdit}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes("TaskTypeDescription") && (
                              <TableCell>
                                <CustomBulkOMTHeader
                                  headerTitle="Task Type"
                                  field="TaskTypeDescription"
                                  type="autocomplete"
                                  headerTooltip="Select Task Type"
                                  onBulkEdit={handleBulkEditWithWarning}
                                  selectedRows={selectedRows}
                                  setSnack={setSnack}
                                  setTaskTypeId={setSelectedTaskTypeId}
                                  value={selectedTaskTypeId}
                                  options={taskOptions}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes("ScheduleStart") && (
                              <TableCell
                                sx={{
                                  minWidth: "200px !important",
                                  width: "200px !important",
                                }}
                              >
                                <CustomBulkOMTHeader
                                  headerTitle="Schedule Start"
                                  field="ScheduleStart"
                                  type="datetime"
                                  selectedRows={selectedRows}
                                  onBulkEdit={handleBulkEdit}
                                  setSnack={setSnack}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes("ScheduleEnd") && (
                              <TableCell
                                sx={{
                                  minWidth: "200px !important",
                                  width: "200px !important",
                                }}
                              >
                                <CustomBulkOMTHeader
                                  headerTitle="Schedule End"
                                  field="ScheduleEnd"
                                  type="datetime"
                                  selectedRows={selectedRows}
                                  onBulkEdit={handleBulkEdit}
                                  setSnack={setSnack}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes("ScheduleStatus") && (
                              <TableCell>
                                <CustomBulkOMTHeader
                                  headerTitle="Schedule Status"
                                  field="ScheduleStatus"
                                  type="autocomplete"
                                  headerTooltip="Select Schedule Status"
                                  onBulkEdit={handleBulkEdit}
                                  selectedRows={selectedRows}
                                  setSnack={setSnack}
                                  options={statusOptions}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes(
                              "ScheduleFailureReason",
                            ) && (
                              <TableCell>
                                <TextLabel text="Schedule Failure Reason" />
                              </TableCell>
                            )}
                            {visibleFields.includes("EXT") && (
                              <TableCell
                                sx={{
                                  textAlign: "center",
                                  minWidth: "40PX !important",
                                  width: "40px !important",
                                }}
                              >
                                <CustomBulkOMTHeader
                                  headerTitle="EXT"
                                  field="EXT"
                                  type="boolean"
                                  onBulkEdit={handleBulkEdit}
                                  selectedRows={selectedRows}
                                  setSnack={setSnack}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes("EXTScheduleStart") && (
                              <TableCell
                                sx={{
                                  minWidth: "200px !important",
                                  width: "200px !important",
                                }}
                              >
                                <CustomBulkOMTHeader
                                  headerTitle="EXT Schedule Start"
                                  field="EXTScheduleStart"
                                  type="datetime"
                                  selectedRows={selectedRows}
                                  onBulkEdit={handleBulkEdit}
                                  setSnack={setSnack}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes("EXTScheduleEnd") && (
                              <TableCell
                                sx={{
                                  minWidth: "200px !important",
                                  width: "200px !important",
                                }}
                              >
                                <CustomBulkOMTHeader
                                  headerTitle="EXT Schedule End"
                                  field="EXTScheduleEnd"
                                  type="datetime"
                                  selectedRows={selectedRows}
                                  onBulkEdit={handleBulkEdit}
                                  setSnack={setSnack}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes("EXTScheduleStatus") && (
                              <TableCell>
                                <CustomBulkOMTHeader
                                  headerTitle="EXT Schedule Status"
                                  field="EXTScheduleStatus"
                                  type="autocomplete"
                                  headerTooltip="Select EXT Schedule Status"
                                  onBulkEdit={handleBulkEdit}
                                  selectedRows={selectedRows}
                                  setSnack={setSnack}
                                  options={statusOptions}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes(
                              "EXTScheduleFailureReason",
                            ) && (
                              <TableCell>
                                <TextLabel text="EXT Failure Reason" />
                              </TableCell>
                            )}
                            {visibleFields.includes("PERR") && (
                              <TableCell
                                sx={{
                                  textAlign: "center",
                                  minWidth: "40PX !important",
                                  width: "40px !important",
                                }}
                              >
                                <CustomBulkOMTHeader
                                  headerTitle="PERR"
                                  field="PERR"
                                  type="boolean"
                                  onBulkEdit={handleBulkEdit}
                                  selectedRows={selectedRows}
                                  setSnack={setSnack}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes("PERRScheduleStart") && (
                              <TableCell
                                sx={{
                                  minWidth: "200px !important",
                                  width: "200px !important",
                                }}
                              >
                                <CustomBulkOMTHeader
                                  headerTitle="PERR Schedule Start"
                                  field="PERRScheduleStart"
                                  type="datetime"
                                  selectedRows={selectedRows}
                                  onBulkEdit={handleBulkEdit}
                                  setSnack={setSnack}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes("PERRScheduleEnd") && (
                              <TableCell
                                sx={{
                                  minWidth: "200px !important",
                                  width: "200px !important",
                                }}
                              >
                                <CustomBulkOMTHeader
                                  headerTitle="PERR Schedule End"
                                  field="PERRScheduleEnd"
                                  type="datetime"
                                  selectedRows={selectedRows}
                                  onBulkEdit={handleBulkEdit}
                                  setSnack={setSnack}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes("PERRScheduleStatus") && (
                              <TableCell>
                                <CustomBulkOMTHeader
                                  headerTitle="PERR Schedule Status"
                                  field="PERRScheduleStatus"
                                  type="autocomplete"
                                  headerTooltip="Select PERR Schedule Status"
                                  onBulkEdit={handleBulkEdit}
                                  selectedRows={selectedRows}
                                  setSnack={setSnack}
                                  options={statusOptions}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes(
                              "PERRScheduleFailureReason",
                            ) && (
                              <TableCell>
                                <TextLabel text="PERR Failure Reason" />
                              </TableCell>
                            )}
                            {visibleFields.includes("Recovery") && (
                              <TableCell
                                sx={{
                                  textAlign: "center",
                                  minWidth: "40PX !important",
                                  width: "40px !important",
                                }}
                              >
                                <CustomBulkOMTHeader
                                  headerTitle="Recovery"
                                  field="Recovery"
                                  type="boolean"
                                  onBulkEdit={handleBulkEdit}
                                  selectedRows={selectedRows}
                                  setSnack={setSnack}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes(
                              "RecoveryScheduleStart",
                            ) && (
                              <TableCell
                                sx={{
                                  minWidth: "200px !important",
                                  width: "200px !important",
                                }}
                              >
                                <CustomBulkOMTHeader
                                  headerTitle="Recovery Schedule Start"
                                  field="RecoveryScheduleStart"
                                  type="datetime"
                                  selectedRows={selectedRows}
                                  onBulkEdit={handleBulkEdit}
                                  setSnack={setSnack}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes("RecoveryScheduleEnd") && (
                              <TableCell
                                sx={{
                                  minWidth: "200px !important",
                                  width: "200px !important",
                                }}
                              >
                                <CustomBulkOMTHeader
                                  headerTitle="Recovery Schedule End"
                                  field="RecoveryScheduleEnd"
                                  type="datetime"
                                  selectedRows={selectedRows}
                                  onBulkEdit={handleBulkEdit}
                                  setSnack={setSnack}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes(
                              "RecoveryScheduleStatus",
                            ) && (
                              <TableCell>
                                <CustomBulkOMTHeader
                                  headerTitle="Recovery Schedule Status"
                                  field="RecoveryScheduleStatus"
                                  type="autocomplete"
                                  headerTooltip="Select Recovery Schedule Status"
                                  onBulkEdit={handleBulkEdit}
                                  selectedRows={selectedRows}
                                  setSnack={setSnack}
                                  options={statusOptions}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes(
                              "RecoveryScheduleFailureReason",
                            ) && (
                              <TableCell>
                                <TextLabel text="Recovery Failure Reason" />
                              </TableCell>
                            )}
                            {visibleFields.includes("TicketNumber") && (
                              <TableCell>
                                <CustomBulkOMTHeader
                                  headerTitle="Ticket number"
                                  field="TicketNumber"
                                  type="textfield"
                                  headerTooltip="Enter Ticket number"
                                  onBulkEdit={handleBulkEdit}
                                  selectedRows={selectedRows}
                                  setSnack={setSnack}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes(
                              "FinalStatusDescription",
                            ) && (
                              <TableCell>
                                <CustomBulkOMTHeader
                                  headerTitle="Final Status"
                                  field="FinalStatusDescription"
                                  type="autocomplete"
                                  headerTooltip="Select Status"
                                  onBulkEdit={handleBulkEdit}
                                  selectedRows={selectedRows}
                                  setSnack={setSnack}
                                  options={statusOptions}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                            {visibleFields.includes("Comments") && (
                              <TableCell>
                                <CustomBulkOMTHeader
                                  headerTitle="Comments"
                                  field="Comments"
                                  type="textfield"
                                  headerTooltip="Enter Comments"
                                  onBulkEdit={handleBulkEdit}
                                  selectedRows={selectedRows}
                                  setSnack={setSnack}
                                  resetValue={headerResetTrigger}
                                />
                              </TableCell>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {displayedRows.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={
                                  visibleFields.length + (editAccess ? 1 : 0)
                                }
                                align="center"
                                sx={{ height: "200px" }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: "Urbanist, sans-serif",
                                    fontSize: "14px",
                                    color: "#666",
                                  }}
                                >
                                  No results found
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            displayedRows.map((row) => {
                              const isItemSelected = isSelected(getRowId(row));
                              return (
                                <TableRow
                                  key={getRowId(row)}
                                  hover
                                  role="checkbox"
                                  aria-checked={isItemSelected}
                                  selected={isItemSelected}
                                >
                                  {editAccess && (
                                    <TableCell
                                      padding="checkbox"
                                      className="sticky-column"
                                    >
                                      <Checkbox
                                        checked={isItemSelected}
                                        onClick={(event) =>
                                          handleRowClick(event, row)
                                        }
                                      />
                                    </TableCell>
                                  )}
                                  <TableCell
                                    className={
                                      editAccess
                                        ? "sticky-column-actions"
                                        : "sticky-column-actions-no-checkbox"
                                    }
                                  >
                                    <Tooltip
                                      title="View History"
                                      placement="top"
                                    >
                                      <IconButton
                                        size="small"
                                        sx={{ left: "12.5px" }}
                                        onClick={(e) =>
                                          handleHistoryClick(e, row)
                                        }
                                      >
                                        <HistoryIcon
                                          color="primary"
                                          fontSize="medium"
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                  {visibleFields.includes("ClassRosterId") &&
                                    !isNoRosterClass && (
                                      <TableCell
                                        sx={{ marginLeft: "10px" }}
                                        className={`sticky-column-rid ${getCellClassName(
                                          row.ClassTraineeId,
                                          "ClassRosterId",
                                        )}`}
                                      >
                                        {row.ClassRosterId}
                                      </TableCell>
                                    )}
                                  {visibleFields.includes("ClassRosterId") &&
                                    isNoRosterClass && (
                                      <TableCell
                                        className={`${editAccess ? "sticky-column-rid no-roster-class" : "sticky-column-rid-no-checkbox no-roster-class"}`}
                                        sx={{ fontSize: "11px" }}
                                      >
                                        {row.NoRosterIdClassName ||
                                          row.noRosterIdClassName ||
                                          ""}
                                      </TableCell>
                                    )}
                                  {visibleFields.includes("EmployeeName") && (
                                    <TableCell
                                      className={`${editAccess ? `sticky-column-1${isNoRosterClass ? " no-roster-class" : ""}` : `sticky-column-1-no-checkbox${isNoRosterClass ? " no-roster-class" : ""}`} ${getCellClassName(
                                        getRowId(row),
                                        "EmployeeName",
                                      )}`}
                                    >
                                      {row.EmployeeName}
                                    </TableCell>
                                  )}
                                  {visibleFields.includes("TraineeStatus") && (
                                    <TableCell
                                      className={`${editAccess ? `sticky-column-2${isNoRosterClass ? " no-roster-class" : ""}` : `sticky-column-2-no-checkbox${isNoRosterClass ? " no-roster-class" : ""}`} ${getCellClassName(
                                        getRowId(row),
                                        "TraineeStatus",
                                      )}`}
                                    >
                                      {isNoRosterClass
                                        ? row.EmployeeStatus ||
                                          row.employeeStatus
                                        : row.TraineeStatus}
                                    </TableCell>
                                  )}
                                  {visibleFields.map((field) => {
                                    if (
                                      field === "ClassRosterId" ||
                                      field === "EmployeeName" ||
                                      field === "TraineeStatus"
                                    )
                                      return null;

                                    const isCheckboxField =
                                      field === "EXT" ||
                                      field === "PERR" ||
                                      field === "Recovery";
                                    const isScheduleField =
                                      field.includes("Schedule") &&
                                      !field.includes("ScheduleFailureReason");

                                    return (
                                      <TableCell
                                        key={field}
                                        className={getCellClassName(
                                          getRowId(row),
                                          field,
                                        )}
                                        onClick={() =>
                                          !isScheduleField &&
                                          handleCellClick(
                                            getRowId(row),
                                            field,
                                            row,
                                          )
                                        }
                                        onDoubleClick={() =>
                                          isScheduleField &&
                                          handleCellDoubleClick(
                                            getRowId(row),
                                            field,
                                            row,
                                          )
                                        }
                                        sx={{
                                          cursor:
                                            isItemSelected && editAccess
                                              ? "pointer"
                                              : "default",
                                          textAlign: isCheckboxField
                                            ? "center"
                                            : "left",
                                          verticalAlign: "middle",
                                          minWidth: "40PX !important",
                                          width: "40px !important",
                                        }}
                                      >
                                        {renderCell(row, field)}
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </StyledTableContainer>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                          fontSize: "12px",
                          letterSpacing: "0.10em",
                        }}
                      >
                        Total records: {rows.length}{" "}
                        {(nameFilterValue || statusFilterValue) &&
                          `| Filtered: ${filteredRows.length}`}
                        {totalUnsavedCells > 0 &&
                          ` | Total unsaved cells: ${totalUnsavedCells}`}
                      </Typography>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={rows.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      width: "100%",
                      marginTop: 1,
                    }}
                  >
                    <CustomOutlinedButton
                      variant="outlined"
                      sx={{ minWidth: 85 }}
                      disabled={!editAccess}
                      onClick={handleCancelButton}
                    >
                      Cancel
                    </CustomOutlinedButton>
                    <CustomButton
                      variant="contained"
                      sx={{ minWidth: 85 }}
                      onClick={handleSaveData}
                      disabled={!editAccess || hasInvalidCells}
                    >
                      Save
                    </CustomButton>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Popover
          open={nameFilterOpen}
          anchorEl={nameFilterAnchorEl}
          onClose={handleNameFilterClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Box sx={{ p: 3, width: 200 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontFamily: "Urbanist, sans-serif",
                fontWeight: "bold",
              }}
            >
              Trainee Name
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "Urbanist, sans-serif",
                    fontWeight: "bold",
                  }}
                >
                  Operator
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={nameFilterOperator}
                  onChange={(e) => setNameFilterOperator(e.target.value)}
                  sx={{ mt: 0.5 }}
                >
                  <MenuItem value="contains">contains</MenuItem>
                  <MenuItem value="equals">equals</MenuItem>
                  <MenuItem value="startsWith">starts with</MenuItem>
                  <MenuItem value="endsWith">ends with</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "Urbanist, sans-serif",
                    fontWeight: "bold",
                  }}
                >
                  Value
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Filter value"
                  value={nameFilterValue}
                  onChange={(e) => setNameFilterValue(e.target.value)}
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                    mt: 1,
                  }}
                >
                  <CustomOutlinedButton
                    size="small"
                    onClick={handleClearNameFilter}
                  >
                    Clear
                  </CustomOutlinedButton>
                  <CustomButton size="small" onClick={handleApplyNameFilter}>
                    Apply
                  </CustomButton>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Popover>

        <Popover
          open={statusFilterOpen}
          anchorEl={statusFilterAnchorEl}
          onClose={handleStatusFilterClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Box sx={{ p: 3, width: 200 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontFamily: "Urbanist, sans-serif",
                fontWeight: "bold",
              }}
            >
              Filter Trainee Status
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "Urbanist, sans-serif",
                    fontWeight: "bold",
                  }}
                >
                  Operator
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={statusFilterOperator}
                  onChange={(e) => setStatusFilterOperator(e.target.value)}
                  sx={{ mt: 0.5 }}
                >
                  <MenuItem value="contains">contains</MenuItem>
                  <MenuItem value="equals">equals</MenuItem>
                  <MenuItem value="startsWith">starts with</MenuItem>
                  <MenuItem value="endsWith">ends with</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "Urbanist, sans-serif",
                    fontWeight: "bold",
                  }}
                >
                  Value
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Filter value"
                  value={statusFilterValue}
                  onChange={(e) => setStatusFilterValue(e.target.value)}
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                    mt: 1,
                  }}
                >
                  <CustomOutlinedButton
                    size="small"
                    onClick={handleClearStatusFilter}
                  >
                    Clear
                  </CustomOutlinedButton>
                  <CustomButton size="small" onClick={handleApplyStatusFilter}>
                    Apply
                  </CustomButton>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Popover>

        <DialogBox
          isOpen={dialogOpen}
          action={dialogAction}
          title={dialogTitle}
          message={dialogMessage}
          messageType={dialogMessageType || "Confirmation"}
          itemList={dialogBulletList}
          isConfirmation={isConfirmationDialog}
        />

        <Dialog
          open={historyOpen}
          onClose={handleHistoryClose}
          scroll="paper"
          TransitionComponent={Transition}
          fullWidth
          maxWidth="md"
          disableEnforceFocus
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <HistoryIcon color="primary" />
              <MainHeading sx={{ marginTop: "5px" }}>
                Change History
              </MainHeading>
            </Box>
            {historyRow && (
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Urbanist, sans-serif",
                  color: "#666",
                  mt: 1,
                  fontSize: "14px",
                }}
              >
                {historyRow.EmployeeName?.split("(")[0]?.trim() ||
                  historyRow.EmployeeName}{" "}
                (ID: {historyRow.OracleId})
              </Typography>
            )}
            <IconButton
              aria-label="close"
              onClick={handleHistoryClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers={true}>
            <Grid container spacing={2}>
              {historyLoading ? (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 4,
                    }}
                  >
                    <Typography>Loading...</Typography>
                  </Box>
                </Grid>
              ) : historyData.length > 0 ? (
                <Grid item xs={12}>
                  <PrimeReactDataGrid
                    size="normal"
                    removableSort
                    resizableColumns
                    showGridlines
                    paginator={true}
                    rows={10}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} record(s)"
                    columnResizeMode="expand"
                    value={historyData}
                    columns={historyColumns}
                    metaKeySelection={false}
                    enableGlobalFilter={false}
                    enableStickyHeader={true}
                    enableScrollable={false}
                    sortMode="multiple"
                    emptyMessage="No records found."
                    alwaysShowPaginator={true}
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="h7"
                      sx={{
                        color: "#3F5765",
                        fontWeight: "bold",
                        letterSpacing: "0.10em",
                        padding: 2,
                      }}
                    >
                      No history data available.
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
        </Dialog>

        <TaskTypeChangeWarning
          open={taskTypeWarningOpen}
          onClose={() => {
            setTaskTypeWarningOpen(false);
            setPendingTaskType(null);
          }}
          onConfirm={confirmTaskTypeChange}
          fieldsToNullify={
            pendingTaskType
              ? getFieldsToNullify(pendingTaskType.value.TaskTypeDescription)
              : []
          }
          newTaskType={pendingTaskType?.value?.TaskTypeDescription || ""}
        />

        <SnackBar snack={snack} snackReset={snackReset} />

        <UploadClassModal
          open={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          onUploadSuccess={handleUploadSuccess}
        />
      </Card>
    </>
  );
};

export default OMTClassSchedule;
